import { useState, useMemo, useCallback, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboardIcon,
  SettingsIcon,
  UserIcon,
  ShieldIcon,
  TrendingUpIcon,
  GaugeIcon,
  BuildingIcon,
  KeyIcon,
  DatabaseIcon,
  FileTextIcon,
  HomeIcon,
  MapPinIcon,
  FileSearchIcon,
  ShieldCheckIcon,
  UsersIcon,
  MenuIcon,
  WalletIcon,
  LayersIcon,
  LifeBuoyIcon,
  BarChart3Icon,
  CreditCardIcon,
  FilterIcon,
  ClipboardListIcon,
  ClockIcon,
  InboxIcon,
  ActivityIcon,
  AlertTriangleIcon,
  ArchiveIcon,
  ArrowLeftRightIcon,
  BookmarkIcon,
  HeartPulseIcon,
  HistoryIcon,
  ImageIcon,
  LinkIcon,
  PieChartIcon,
  ServerIcon,
  StoreIcon,
  TagIcon,
  TargetIcon,
  WebhookIcon,
} from 'lucide-react';
import {
  Alert,
  AppHeader,
  PortalFrame,
  BuildVersion,
  Button,
  CommandPalette,
  CommandPaletteTrigger,
  OrgSwitcher,
  OrgSwitcherDialog,
  IdleSignOut,
  ThemeToggle,
  UserProfileMenu,
  NotificationPanel,
  RoleBasedNav,
  NavBadge,
} from '@aaimplatform/ui';
import type {
  PaletteResult,
  PaletteCategory,
  Organization,
  UserProfile,
  UserMenuItem,
  Notification,
  BreadcrumbItem,
  NavGroupDefinition,
  NavItemDefinition,
} from '@aaimplatform/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { IDLE_SIGN_OUT_CHANNEL } from '../stores/idleSignOut';
import { resolveOrgContext } from './org-context';
import { useTaskCount } from '../hooks/useTasks';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '../hooks/useNotifications';
import { useIdpPendingClaimCount } from '../hooks/useIdpPendingClaims';
import { useAccessibleTenants } from '../hooks/useTenants';
import { useInstitutionFlags, DEFAULT_INSTITUTION_FLAGS } from '../hooks/useInstitutionFlags';
import type { InstitutionFlags } from '../hooks/useInstitutionFlags';
import { tenantsApi } from '../api/services/tenants';
import { PLATFORM_ADMIN_ROLES, hasPlatformAdminRole } from '../lib/platform-admin-roles';
import { PE_USER_ROLES } from '../lib/pe-roles';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Sidebar width now lives with the frame that applies it: PORTAL_SIDEBAR_WIDTH
// in @aaimplatform/ui (packages/ui/src/shell/PortalFrame.tsx). Same 260px.

// ---------------------------------------------------------------------------
// Page Info for Breadcrumbs
// ---------------------------------------------------------------------------

interface PageInfo {
  title: string;
  breadcrumbs: BreadcrumbItem[];
}

function buildBreadcrumbs(labels: string[], paths?: (string | undefined)[]): BreadcrumbItem[] {
  return labels.map((label, index) => {
    const isLast = index === labels.length - 1;
    return {
      label,
      href: isLast ? undefined : paths?.[index],
    };
  });
}

const PAGE_INFO: Record<string, PageInfo> = {
  '/': {
    title: 'Dashboard',
    breadcrumbs: buildBreadcrumbs(['Dashboard']),
  },
  '/dashboards/loan-pipeline': {
    title: 'Loan Pipeline',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Dashboards', 'Loan Pipeline'], ['/', undefined]),
  },
  // '/dashboards/collateral-monitoring' redirects to /monitoring (routes/index.tsx).
  // It mounted the same component under a second title, 'Collateral
  // Monitoring', beside the nav's 'Health Overview' (r3-fi-cons-11).
  '/dashboards/compliance-status': {
    title: 'Compliance Status',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Dashboards', 'Compliance Status'], ['/', undefined]),
  },
  '/dashboards/risk': {
    title: 'Risk Dashboard',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Compliance', 'Risk Dashboard'], ['/', undefined]),
  },
  '/pledges': {
    title: 'Pledge Inbox',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Operations', 'Pledge Inbox'], ['/', undefined]),
  },
  '/pledges/active': {
    title: 'Active Pledges',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Operations', 'Active Pledges'], ['/', undefined]),
  },
  '/pledges/:pledgeId': {
    title: 'Pledge Detail',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Operations', 'Pledge Inbox', 'Pledge Detail'], ['/', undefined, '/pledges']),
  },
  // fi-alerts-scope (operator ruling): the page lists compliance alerts only.
  // ORANGE and RED Collateral Health Alert tier transitions join it after
  // GH #2384; the title names what the page holds until then.
  '/alerts': {
    title: 'Compliance Alerts',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Operations', 'Compliance Alerts'], ['/', undefined]),
  },
  '/notifications': {
    title: 'Notifications',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Operations', 'Notifications'], ['/', undefined]),
  },
  '/tasks': {
    title: 'FI Tasks',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Tasks'], ['/']),
  },
  '/loans/active': {
    title: 'Active Loans',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Loans', 'Active Loans'], ['/']),
  },
  '/portfolio': {
    title: 'Portfolio',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Portfolio'], ['/']),
  },
  '/collateral/signatures': {
    title: 'Signatures',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral', 'Signatures'], ['/']),
  },
  '/collateral/ucc-filings': {
    title: 'UCC Filings',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Lien Filings', 'UCC Filings'], ['/', undefined]),
  },
  '/collateral/property-valuations': {
    title: 'Property Valuations',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral', 'Property Valuations'], ['/']),
  },
  '/collateral/title-searches': {
    title: 'Title Searches',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral', 'Title Searches'], ['/']),
  },
  '/collateral/lien-verifications': {
    title: 'Lien Verifications',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral', 'Lien Verifications'], ['/']),
  },
  '/collateral/property-profile': {
    title: 'Property Profile',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral', 'Property Profile'], ['/']),
  },
  // fi-person-noun-and-signature-label (operator ruling): FI staff read
  // 'customer' for the person; 'Borrower' stays only in loan-context columns.
  '/members': {
    title: 'Customer Lookup',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Customers'], ['/']),
  },
  '/compliance': {
    title: 'Compliance Reports',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Compliance', 'Reports'], ['/']),
  },
  '/profile': {
    title: 'Profile',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Profile'], ['/']),
  },
  // '/policy' redirects to its first tab, Asset Class Eligibility; both keys
  // carry the nav label so the top bar and the sidebar name one page.
  '/policy': {
    title: 'Asset Class Eligibility',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Policy', 'Asset Class Eligibility'], ['/']),
  },
  '/policy/asset-classes': {
    title: 'Asset Class Eligibility',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Policy', 'Asset Class Eligibility'], ['/']),
  },
  '/policy/cha-thresholds': {
    title: 'Advance Rate Programs',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Policy', 'Advance Rate Programs'], ['/']),
  },
  '/policy/concentration': {
    title: 'Concentration Limits',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Policy', 'Concentration Limits'], ['/']),
  },
  '/policy/monitoring': {
    title: 'Monitoring & Alerts',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Policy', 'Monitoring & Alerts'], ['/']),
  },
  '/policy/audit': {
    title: 'Policy Audit Trail',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Policy', 'Policy Audit Trail'], ['/']),
  },
  // Overdraft-coverage arc OC-3 -- member-liquidity policy chassis surfaces
  // (conditional on enableOverdraftPreview)
  '/tempo': {
    title: 'Coverage Policy',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Aaim Tempo', 'Coverage Policy'], ['/']),
  },
  '/tempo/pool-appetite': {
    title: 'Pool Appetite Policy',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Aaim Tempo', 'Pool Appetite Policy'], ['/']),
  },
  // One entry serves both /tempo/accounts and /tempo/accounts/:accountId: the
  // resolver below tries the exact pathname, then pops one segment at a time,
  // so the detail route lands here rather than on '/tempo'. A literal
  // '/tempo/accounts/:accountId' key would be dead -- that walker builds its
  // test paths from URL segments and never produces a ':param' key.
  '/tempo/accounts': {
    title: 'Account Decision',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Aaim Tempo', 'Account Decision'], ['/']),
  },
  // Core deposit feed (GH #2911). One entry serves the delivery detail route
  // too, by the segment-popping resolver described above. No nav group in the
  // trail: the screens sit outside the FI_ADMIN-only Tempo group so every
  // FEED_READ_ROLES role reaches them.
  '/core-deposit-feed': {
    title: 'Core Deposit Feed',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Core Deposit Feed'], ['/']),
  },
  // Economic Modeling (GH #2727) -- conditional on enableOverdraftPreview.
  // NOT "Aaim Foresight": operator ruling 2026-09-21 (GH #2915) reserves
  // Foresight for depositor-level decisioning per tempo-design.md 12.2.
  '/modeling/scenarios': {
    title: 'Economic Modeling',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Economic Modeling'], ['/']),
  },
  // F-PORTAL-AUDIENCE-SPLIT (2026-05-17): platform-scoped breadcrumbs removed.
  // Tenant Management, All Institutions, Platform Configuration (12 sub-paths),
  // Notification Routing, System Health, and Audit Logs are platform-operator
  // surfaces and live exclusively in platform-admin (port 9303). Their legacy
  // /admin/* paths now redirect cross-origin via PlatformAdminRedirect in
  // routes/index.tsx -- no breadcrumbs render here because admin-portal never
  // displays the page.
  // '/admin/integrations' breadcrumb removed 2026-05-04 (Sprint C17+1):
  // route deleted alongside the deprecated PlatformDataProvider model.
  // The /admin/integrations/:integrationId detail route was deleted 2026-09-15 (SPA nav census): its list page was gone since C17+1, so nothing reached it.
  // GH #676 (2026-08-15 operator ruling): the standalone "PE Valuation" nav
  // group is retired. These three pages now live inside their destination
  // groups (Fund Administration + Configuration under FI Configuration;
  // Collateral Monitoring under Collateral Monitoring) -- breadcrumbs updated
  // to match. /pe/funds carries its nav label, 'PE Fund Administration', as
  // its title and breadcrumb leaf, as its two siblings already did
  // (r3-fi-g5-6).
  '/pe/funds': {
    title: 'PE Fund Administration',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'PE Fund Administration'], ['/', undefined]),
  },
  '/pe/collateral-monitoring': {
    title: 'PE Collateral Monitoring',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'PE Collateral Monitoring'], ['/', undefined]),
  },
  '/pe/configuration': {
    title: 'PE Configuration',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'PE Configuration'], ['/', undefined]),
  },
  // Sprint C14 -- Collateral Monitoring group
  '/monitoring': {
    title: 'Health Overview',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'Health Overview'], ['/', undefined]),
  },
  '/monitoring/classification': {
    title: 'Asset Classification',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'Asset Classification'], ['/', undefined]),
  },
  '/monitoring/valuation-history': {
    title: 'Valuation History',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'Valuation History'], ['/', undefined]),
  },
  '/monitoring/concentration': {
    title: 'Concentration Risk',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'Concentration Risk'], ['/', undefined]),
  },
  '/monitoring/custodian-status': {
    title: 'Custodian Status',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'Custodian Status'], ['/', undefined]),
  },
  '/admin/webhook-deliveries': {
    title: 'Webhook Delivery Log',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Collateral Monitoring', 'Webhook Delivery Log'], ['/', undefined]),
  },
  // Sprint B Lane B2 (2026-05-17) -- Lien Filings group default leaf:
  // /lien/awaiting-review renders the existing UCCFilings page with a
  // pre-applied status=AWAITING_REVIEW filter. The auto-populated index of
  // FI pledge approval flow output for FI staff to review before submission.
  '/lien/awaiting-review': {
    title: 'Filings Awaiting Review',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Lien Filings', 'Filings Awaiting Review'], ['/', undefined]),
  },
  // Sprint C14 -- Lien Filings group
  '/lien/filing-status': {
    title: 'Filing Status',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Lien Filings', 'Filing Status'], ['/', undefined]),
  },
  '/lien/jurisdiction-reports': {
    title: 'Jurisdiction Reports',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Lien Filings', 'Jurisdiction Reports'], ['/', undefined]),
  },
  '/lien/fee-reconciliation': {
    title: 'Fee Reconciliation',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Lien Filings', 'Fee Reconciliation'], ['/', undefined]),
  },
  // Sprint C14 -- Loan Participations group (conditional on enableLoanParticipations)
  '/syndication/marketplace': {
    title: 'Marketplace',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Loan Participations', 'Marketplace'], ['/', undefined]),
  },
  '/syndication/list-a-loan': {
    title: 'List a Loan',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Loan Participations', 'List a Loan'], ['/', undefined]),
  },
  '/syndication/compare': {
    title: 'Compare deals',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Loan Participations', 'Marketplace', 'Compare'],
      ['/', undefined, '/syndication/marketplace', undefined],
    ),
  },
  '/syndication/offerings': {
    title: 'Offering',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Loan Participations', 'Marketplace', 'Offering'],
      ['/', undefined, '/syndication/marketplace', undefined],
    ),
  },
  '/syndication/indications': {
    title: 'Indications of Interest',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Loan Participations', 'Indications of Interest'],
      ['/', undefined],
    ),
  },
  '/syndication/commitments': {
    title: 'Commitments',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Loan Participations', 'Commitments'], ['/', undefined]),
  },
  '/syndication/positions': {
    title: 'Positions',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Loan Participations', 'Positions'], ['/', undefined]),
  },
  '/syndication/risk-capital': {
    title: 'Risk Capital',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Loan Participations', 'Risk Capital'], ['/', undefined]),
  },
  // Sprint C14 -- Audit & Compliance group
  '/audit/pledges': {
    title: 'Pledge Audit Trail',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Compliance', 'Pledge Audit Trail'], ['/', undefined]),
  },
  '/audit/regulatory-readiness': {
    title: 'Regulatory Readiness',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Compliance', 'Regulatory Readiness'], ['/', undefined]),
  },
  '/audit/examiner-evidence': {
    title: 'Examiner Evidence',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Compliance', 'Examiner Evidence'], ['/', undefined]),
  },
  // Sprint C14 -- Configuration group
  '/admin/config/organization': {
    title: 'Organization Profile',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Organization Profile'], ['/', undefined]),
  },
  '/admin/config/webhook-endpoints': {
    title: 'Webhook Endpoints',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Webhook Endpoints'], ['/', undefined]),
  },
  '/admin/config/custodian-connections': {
    title: 'Custodian Connections',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Custodian Connections'], ['/', undefined]),
  },
  '/admin/config/branding': {
    title: 'Consumer Portal Branding',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Consumer Portal Branding'], ['/', undefined]),
  },
  '/admin/config/landing-copy': {
    title: 'Landing Page Copy',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Landing Page Copy'], ['/', undefined]),
  },
  '/admin/config/workflow-ordering': {
    title: 'Workflow Ordering',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Workflow Ordering'], ['/', undefined]),
  },
  '/admin/config/sla': {
    title: 'SLA Settings',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'SLA Settings'], ['/', undefined]),
  },
  '/admin/config/lending': {
    title: 'Lending Configuration',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Configuration', 'Lending Configuration'], ['/', undefined]),
  },
  // Sprint C14 -- Administration group
  '/admin/users': {
    title: 'User Management',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Administration', 'User Management'], ['/', undefined]),
  },
  '/admin/staff/:staffId': {
    title: 'Staff Detail',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'User Management', 'Staff Detail'],
      ['/', undefined, '/admin/users'],
    ),
  },
  '/admin/api-keys': {
    title: 'API Keys',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Administration', 'API Keys'], ['/', undefined]),
  },
  // Sprint 5 (FI Staff Authn/Z) -- Identity / RBAC / ABAC surfaces
  '/admin/idp/pending-claims': {
    title: 'Pending IdP Claims',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'Pending IdP Claims'],
      ['/', undefined],
    ),
  },
  '/admin/idp/role-mappings': {
    title: 'IdP Role Mappings',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'IdP Role Mappings'],
      ['/', undefined],
    ),
  },
  '/admin/abac-policies': {
    title: 'ABAC Policies',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'ABAC Policies'],
      ['/', undefined],
    ),
  },
  '/admin/examiners': {
    title: 'Examiners',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'Examiners'],
      ['/', undefined],
    ),
  },
  // GH #1729 deliverable 2(a), 2026-09-15. The /admin/support routes had
  // shipped since the W2d support arc with no nav entry, no palette entry and
  // no breadcrumb for any role -- reachable only by typing the URL. The nav
  // item below points at the list; the leaves get breadcrumbs so an
  // operator who lands on one is not stranded at the "Page" fallback.
  // GH #678 (operator ruling 2026-10-23) removed /admin/support/agents and
  // /admin/support/sla; that tooling lives in platform-admin.
  '/admin/support': {
    title: 'Support Cases',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'Support Cases'],
      ['/', undefined],
    ),
  },
  '/admin/support/new': {
    title: 'New Support Case',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'Support Cases', 'New Case'],
      ['/', undefined, '/admin/support'],
    ),
  },
  '/admin/support/:caseId': {
    title: 'Support Case',
    breadcrumbs: buildBreadcrumbs(
      ['Dashboard', 'Administration', 'Support Cases', 'Case Detail'],
      ['/', undefined, '/admin/support'],
    ),
  },
};

// ---------------------------------------------------------------------------
// Search Configuration
// ---------------------------------------------------------------------------

// F-PORTAL-AUDIENCE-SPLIT (2026-05-17): the 'tenants' search category was
// removed -- it surfaced platform-admin's domain (Tenant Management, All
// Institutions). FI Admin's Cmd+K palette is scoped to the FI tenant.
const SEARCH_CATEGORIES: PaletteCategory[] = [
  { id: 'pages', label: 'Pages', icon: FileTextIcon },
  { id: 'loans', label: 'Loans', icon: WalletIcon },
  { id: 'borrowers', label: 'Customers', icon: UsersIcon },
  { id: 'compliance', label: 'Compliance', icon: ShieldIcon },
];

/**
 * Static page search entries. In production these would come from an API.
 *
 * Exported so the reachability gate below can be pinned against the WHOLE list
 * rather than against a hand-typed count -- a literal here would go stale the
 * first time an entry is added.
 */
export const SEARCHABLE_PAGES: { id: string; title: string; category: string; href: string }[] = [
  // Pages
  { id: 'dashboard', title: 'Dashboard', category: 'pages', href: '/' },
  { id: 'pledge-inbox', title: 'Pledge Inbox', category: 'pages', href: '/pledges' },
  { id: 'pledge-active', title: 'Active Pledges', category: 'pages', href: '/pledges/active' },
  { id: 'alerts', title: 'Compliance Alerts', category: 'pages', href: '/alerts' },
  { id: 'tasks', title: 'FI Tasks', category: 'pages', href: '/tasks' },
  // Collateral Monitoring
  { id: 'monitoring', title: 'Health Overview', category: 'pages', href: '/monitoring' },
  { id: 'monitoring-classification', title: 'Asset Classification', category: 'pages', href: '/monitoring/classification' },
  { id: 'monitoring-valuation-history', title: 'Valuation History', category: 'pages', href: '/monitoring/valuation-history' },
  { id: 'monitoring-concentration', title: 'Concentration Risk', category: 'pages', href: '/monitoring/concentration' },
  { id: 'monitoring-custodian-status', title: 'Custodian Status', category: 'pages', href: '/monitoring/custodian-status' },
  { id: 'webhook-deliveries', title: 'Webhook Delivery Log', category: 'pages', href: '/admin/webhook-deliveries' },
  // Lien Filings
  { id: 'lien-awaiting-review', title: 'Filings Awaiting Review', category: 'pages', href: '/lien/awaiting-review' },
  { id: 'ucc-filings', title: 'UCC Filings', category: 'pages', href: '/collateral/ucc-filings' },
  { id: 'lien-filing-status', title: 'Filing Status', category: 'pages', href: '/lien/filing-status' },
  { id: 'lien-jurisdiction-reports', title: 'Jurisdiction Reports', category: 'pages', href: '/lien/jurisdiction-reports' },
  { id: 'lien-fee-reconciliation', title: 'Fee Reconciliation', category: 'pages', href: '/lien/fee-reconciliation' },
  // Loan Participations
  { id: 'syndication-marketplace', title: 'Loan Participations Marketplace', category: 'pages', href: '/syndication/marketplace' },
  { id: 'syndication-list-a-loan', title: 'List a Loan', category: 'pages', href: '/syndication/list-a-loan' },
  { id: 'syndication-indications', title: 'Indications of Interest', category: 'pages', href: '/syndication/indications' },
  { id: 'syndication-commitments', title: 'Commitments', category: 'pages', href: '/syndication/commitments' },
  { id: 'syndication-positions', title: 'Positions', category: 'pages', href: '/syndication/positions' },
  { id: 'syndication-risk-capital', title: 'Risk Capital', category: 'pages', href: '/syndication/risk-capital' },
  // Audit & Compliance
  { id: 'audit-pledges', title: 'Pledge Audit Trail', category: 'compliance', href: '/audit/pledges' },
  { id: 'compliance-reports', title: 'Compliance Reports', category: 'pages', href: '/compliance' },
  { id: 'risk-dashboard', title: 'Risk Dashboard', category: 'pages', href: '/dashboards/risk' },
  { id: 'audit-regulatory-readiness', title: 'Regulatory Readiness', category: 'compliance', href: '/audit/regulatory-readiness' },
  { id: 'audit-examiner-evidence', title: 'Examiner Evidence Package', category: 'compliance', href: '/audit/examiner-evidence' },
  // Configuration
  { id: 'admin-config-organization', title: 'Organization Profile', category: 'pages', href: '/admin/config/organization' },
  { id: 'admin-config-webhook-endpoints', title: 'Webhook Endpoints', category: 'pages', href: '/admin/config/webhook-endpoints' },
  { id: 'policy-cha-thresholds', title: 'Advance Rate Programs', category: 'pages', href: '/policy/cha-thresholds' },
  { id: 'policy-concentration', title: 'Concentration Limits', category: 'pages', href: '/policy/concentration' },
  { id: 'policy-asset-classes', title: 'Asset Class Eligibility', category: 'pages', href: '/policy/asset-classes' },
  { id: 'admin-config-custodian-connections', title: 'Custodian Connections', category: 'pages', href: '/admin/config/custodian-connections' },
  { id: 'admin-config-branding', title: 'Consumer Portal Branding', category: 'pages', href: '/admin/config/branding' },
  { id: 'admin-config-landing-copy', title: 'Landing Page Copy', category: 'pages', href: '/admin/config/landing-copy' },
  { id: 'admin-config-workflow-ordering', title: 'Workflow Ordering', category: 'pages', href: '/admin/config/workflow-ordering' },
  { id: 'admin-config-sla', title: 'SLA Settings', category: 'pages', href: '/admin/config/sla' },
  { id: 'admin-config-lending', title: 'Lending Configuration', category: 'pages', href: '/admin/config/lending' },
  { id: 'policy-monitoring', title: 'Monitoring & Alerts', category: 'pages', href: '/policy/monitoring' },
  { id: 'policy-audit', title: 'Policy Audit Trail', category: 'compliance', href: '/policy/audit' },
  // Aaim Tempo (overdraft-coverage arc OC-3; flag-gated surfaces)
  { id: 'policy-member-liquidity', title: 'Aaim Tempo Coverage Policy', category: 'pages', href: '/tempo' },
  { id: 'policy-pool-appetite', title: 'Aaim Tempo Pool Appetite Policy', category: 'pages', href: '/tempo/pool-appetite' },
  { id: 'tempo-accounts', title: 'Aaim Tempo Account Decision', category: 'pages', href: '/tempo/accounts' },
  // Administration
  { id: 'admin-users', title: 'User Management', category: 'pages', href: '/admin/users' },
  { id: 'admin-api-keys', title: 'API Keys', category: 'pages', href: '/admin/api-keys' },
  { id: 'admin-idp-pending-claims', title: 'Pending IdP Claims', category: 'pages', href: '/admin/idp/pending-claims' },
  { id: 'admin-idp-role-mappings', title: 'IdP Role Mappings', category: 'pages', href: '/admin/idp/role-mappings' },
  { id: 'admin-abac-policies', title: 'ABAC Policies', category: 'pages', href: '/admin/abac-policies' },
  { id: 'admin-examiners', title: 'Examiners', category: 'pages', href: '/admin/examiners' },
  // GH #1729 deliverable 2(a): the support tree was absent from the palette.
  // Only the list is indexed -- /new, /agents, /sla and /:caseId are reached
  // from inside it, and an entry whose href is in no nav group is filtered out
  // by the reachability gate below anyway.
  { id: 'admin-support', title: 'Support Cases', category: 'pages', href: '/admin/support' },
  // PE Valuation surfaces (GH #676: relocated out of the standalone "PE
  // Valuation" nav group into FI Configuration / Collateral Monitoring;
  // these search-index entries are unaffected -- they index the page, not
  // the nav placement, and the routes/index.tsx PeFeatureGate still governs
  // reachability).
  { id: 'pe-funds', title: 'PE Fund Administration', category: 'pages', href: '/pe/funds' },
  { id: 'pe-collateral', title: 'PE Collateral Monitoring', category: 'pages', href: '/pe/collateral-monitoring' },
  { id: 'pe-config', title: 'PE Configuration', category: 'pages', href: '/pe/configuration' },
  // Lending (Preview)
  { id: 'loan-pipeline', title: 'Loan Pipeline', category: 'loans', href: '/dashboards/loan-pipeline' },
  { id: 'active-loans', title: 'Active Loans', category: 'loans', href: '/loans/active' },
  { id: 'portfolio', title: 'Portfolio', category: 'pages', href: '/portfolio' },
  { id: 'member-lookup', title: 'Customer Lookup', category: 'borrowers', href: '/members' },
  { id: 'collateral-sigs', title: 'Signatures', category: 'pages', href: '/collateral/signatures' },
  { id: 'property-vals', title: 'Property Valuations', category: 'pages', href: '/collateral/property-valuations' },
  { id: 'title-searches', title: 'Title Searches', category: 'pages', href: '/collateral/title-searches' },
  { id: 'lien-verifications', title: 'Lien Verifications', category: 'pages', href: '/collateral/lien-verifications' },
  { id: 'property-profiles', title: 'Property Profile', category: 'pages', href: '/collateral/property-profile' },
  // Platform admin (preserved)
  // F-PORTAL-AUDIENCE-SPLIT (2026-05-17): the four platform-operator entries
  // that lived here (Tenant Management, All Institutions, System Health, Audit
  // Logs) were removed -- they surface platform-admin's domain. FI Admin's
  // Cmd+K palette must not navigate to those surfaces.
  // 'data-integrations' search entry removed 2026-05-04 (Sprint C17+1).
  // F-PORTAL-AUDIENCE-SPLIT (2026-05-17): Lending Configuration and
  // Notification Routing entries removed -- platform-admin owns those surfaces.
  // Compliance items (searchable)
  { id: 'compliance-regu', title: 'Regulation U Compliance', category: 'compliance', href: '/compliance' },
  // Repointed 2026-09-15 (GH #1729 deliverable 2(b)) from
  // /dashboards/collateral-monitoring to /monitoring. Both paths render the
  // SAME component (routes/index.tsx: CollateralMonitoringDashboard is mounted
  // at both), but only /monitoring is a nav target -- 'health-overview' /
  // 'monitoring' in NAV_ID_TO_PATH. The alias path is in no nav group for any
  // role, so under the reachability gate this entry would have vanished from
  // every palette. Same destination on screen, now reachable.
  { id: 'compliance-ltv', title: 'LTV Breach Alerts', category: 'compliance', href: '/monitoring' },
  { id: 'compliance-concentration', title: 'Concentration Risk', category: 'compliance', href: '/dashboards/risk' },
];

// ---------------------------------------------------------------------------
// Navigation Group Definitions (RoleBasedNav)
// ---------------------------------------------------------------------------

const NAV_ID_TO_PATH: Record<string, string> = {
  'dashboard': '/',
  // Sprint C14 -- pledges retains /pledges as the Pledge Inbox route; the
  // historical PledgeQueue.tsx is renamed to PledgeInbox.tsx in WP B-1.
  'pledges': '/pledges',
  'pledges-active': '/pledges/active',
  'alerts': '/alerts',
  'tasks': '/tasks',
  // Lending (Preview) -- conditional on enableLosPreview
  'dashboards-loan-pipeline': '/dashboards/loan-pipeline',
  'dashboards-compliance-status': '/dashboards/compliance-status',
  'dashboards-risk': '/dashboards/risk',
  'loans-active': '/loans/active',
  'portfolio': '/portfolio',
  'collateral-signatures': '/collateral/signatures',
  'collateral-property-valuations': '/collateral/property-valuations',
  'collateral-title-searches': '/collateral/title-searches',
  'collateral-lien-verifications': '/collateral/lien-verifications',
  'collateral-property-profiles': '/collateral/property-profile',
  'members': '/members',
  // Lien Filings
  'lien-awaiting-review': '/lien/awaiting-review',
  'collateral-ucc-filings': '/collateral/ucc-filings',
  'lien-filing-status': '/lien/filing-status',
  'lien-jurisdiction-reports': '/lien/jurisdiction-reports',
  'lien-fee-reconciliation': '/lien/fee-reconciliation',
  // Audit & Compliance
  'compliance': '/compliance',
  'audit-pledges': '/audit/pledges',
  'audit-regulatory-readiness': '/audit/regulatory-readiness',
  'audit-examiner-evidence': '/audit/examiner-evidence',
  // Alias used by the regulatory-readiness page component for nav selection
  'regulatory-readiness': '/audit/regulatory-readiness',
  // Configuration -- existing collateral-policy pages reused under new group
  'policy-asset-classes': '/policy/asset-classes',
  'policy-cha-thresholds': '/policy/cha-thresholds',
  'policy-concentration': '/policy/concentration',
  'policy-monitoring': '/policy/monitoring',
  'policy-audit': '/policy/audit',
  // Member Liquidity policy chassis -- conditional on enableOverdraftPreview
  'policy-member-liquidity': '/tempo',
  'policy-pool-appetite': '/tempo/pool-appetite',
  'tempo-accounts': '/tempo/accounts',
  // FI Economic Modeling -- conditional on enableOverdraftPreview
  'modeling-scenarios': '/modeling/scenarios',
  'admin-config-organization': '/admin/config/organization',
  'admin-config-webhook-endpoints': '/admin/config/webhook-endpoints',
  'admin-config-custodian-connections': '/admin/config/custodian-connections',
  'admin-config-branding': '/admin/config/branding',
  'admin-config-landing-copy': '/admin/config/landing-copy',
  'admin-config-workflow-ordering': '/admin/config/workflow-ordering',
  'admin-config-sla': '/admin/config/sla',
  'admin-config-lending': '/admin/config/lending',
  // Administration
  'admin-users': '/admin/users',
  'admin-examiners': '/admin/examiners',
  'admin-support': '/admin/support',
  'admin-api-keys': '/admin/api-keys',
  // Sprint 5 (FI Staff Authn/Z) -- Identity / RBAC / ABAC surfaces
  'admin-idp-pending-claims': '/admin/idp/pending-claims',
  'admin-idp-role-mappings': '/admin/idp/role-mappings',
  'admin-abac-policies': '/admin/abac-policies',
  // Collateral Monitoring
  'monitoring': '/monitoring',
  // Alias used by the health-overview / collateral-monitoring route alias
  'health-overview': '/monitoring',
  'monitoring-classification': '/monitoring/classification',
  'monitoring-valuation-history': '/monitoring/valuation-history',
  'monitoring-concentration': '/monitoring/concentration',
  'monitoring-custodian-status': '/monitoring/custodian-status',
  'admin-webhook-deliveries': '/admin/webhook-deliveries',
  // Loan Participations -- conditional on enableLoanParticipations
  'syndication-marketplace': '/syndication/marketplace',
  'syndication-list-a-loan': '/syndication/list-a-loan',
  'syndication-indications': '/syndication/indications',
  'syndication-commitments': '/syndication/commitments',
  'syndication-positions': '/syndication/positions',
  'syndication-risk-capital': '/syndication/risk-capital',
  // Platform-operator surfaces: ALL stale NAV_ID_TO_PATH entries left from the
  // F-PORTAL-AUDIENCE-SPLIT were removed by the nav-tree audit (#1729) -- the 13
  // dead admin-* / admin-platform-* entries first, then admin-settings /
  // admin-platform-security / admin-audit once their last live callers (the
  // Dashboard "Settings" button and the Examiners "Audit Logs" anchor) were
  // removed as 404-ing dead-links. platform-admin at 9303 owns these surfaces.
  // A real FI-admin Controls/Policy entry point is a separate future add (#1729).
  // 'admin-integrations' nav id removed 2026-05-04 (Sprint C17+1).
  // PE Valuation -- conditional on enablePeValuation. GH #676: items now
  // live inside the 'configuration' (pe-funds, pe-configuration) and
  // 'collateral-monitoring' (pe-collateral-monitoring) nav groups instead of
  // a standalone group; the flag still gates their inclusion at item level.
  'pe-funds': '/pe/funds',
  'pe-collateral-monitoring': '/pe/collateral-monitoring',
  'pe-configuration': '/pe/configuration',
};

// ---------------------------------------------------------------------------
// Active nav item resolution
// ---------------------------------------------------------------------------

/**
 * Routes that have no nav item of their own, mapped to the nav path of the
 * list they belong to. A generic prefix match covers detail routes nested
 * under a nav path (/pledges/:id under /pledges, /syndication/positions/:id
 * under /syndication/positions); these are the child routes whose parent is a
 * sibling path, which no prefix match can find.
 */
const CHILD_ROUTE_PARENTS: ReadonlyArray<readonly [prefix: string, parentPath: string]> = [
  ['/syndication/compare', '/syndication/marketplace'],
  ['/syndication/offerings', '/syndication/marketplace'],
  ['/admin/staff', '/admin/users'],
];

/** The nav id whose NAV_ID_TO_PATH entry is exactly `path`, or null. */
function navIdForExactPath(path: string): string | null {
  if (path === '/') return 'dashboard';
  // The slug form is the id convention for most items; prefer it where it is
  // an entry for this path, so an alias id sharing the path never wins.
  const slug = path.replace(/^\//, '').replace(/\//g, '-');
  if (NAV_ID_TO_PATH[slug] === path) return slug;
  for (const [id, navPath] of Object.entries(NAV_ID_TO_PATH)) {
    if (navPath === path) return id;
  }
  return null;
}

/** Reads `from.pathname` off a router location state, when one is there. */
function originPathname(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const from = (state as { from?: unknown }).from;
  if (typeof from === 'string') return from;
  if (typeof from === 'object' && from !== null) {
    const pathname = (from as { pathname?: unknown }).pathname;
    if (typeof pathname === 'string') return pathname;
  }
  return null;
}

/**
 * The sidebar item a route belongs to. The previous derivation turned the
 * pathname into a slug ('/pledges/<uuid>' -> 'pledges-<uuid>') and highlighted
 * nothing whenever the slug was not an id: every detail page, Compare deals,
 * and the property profile (id 'collateral-property-profiles', path
 * '/collateral/property-profile').
 *
 * Order: the exact nav path; then, on a route with no nav item of its own, the
 * list the operator came from (`location.state.from`, which the pledge lists
 * set when they open a detail); then a declared child route; then the longest
 * nav path the route is nested under. The slug stays the last resort.
 */
export function resolveActiveNavItemId(pathname: string, state?: unknown): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  const exact = navIdForExactPath(path);
  if (exact) return exact;

  const origin = originPathname(state);
  if (origin) {
    const fromId = navIdForExactPath(
      origin.length > 1 ? origin.replace(/\/+$/, '') : origin,
    );
    if (fromId) return fromId;
  }

  for (const [prefix, parentPath] of CHILD_ROUTE_PARENTS) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      const parentId = navIdForExactPath(parentPath);
      if (parentId) return parentId;
    }
  }

  let bestPath = '';
  for (const navPath of Object.values(NAV_ID_TO_PATH)) {
    if (navPath !== '/' && path.startsWith(`${navPath}/`) && navPath.length > bestPath.length) {
      bestPath = navPath;
    }
  }
  if (bestPath) {
    const parentId = navIdForExactPath(bestPath);
    if (parentId) return parentId;
  }

  return path.replace(/^\//, '').replace(/\//g, '-');
}

/**
 * The top-bar title and breadcrumbs for a route.
 *
 * A pledge detail is origin-aware: opened from Active Pledges, FI Tasks or
 * Alerts, the chain mirrors that list. With no origin (a deep link, a reload)
 * it resolves to the '/pledges/:pledgeId' entry. That entry is a literal key
 * the prefix walker below never produces, so before fi-g1-8 a deep-linked
 * detail walked up to '/pledges' and read "Pledge Inbox".
 */
export function resolvePageInfo(pathname: string, state?: unknown): PageInfo {
  if (/^\/pledges\/[^/]+\/?$/.test(pathname) && !/^\/pledges\/(active|queue)\/?$/.test(pathname)) {
    const originPath = originPathname(state);
    if (originPath === '/pledges/active') {
      return {
        title: 'Pledge Detail',
        breadcrumbs: buildBreadcrumbs(
          ['Dashboard', 'Operations', 'Active Pledges', 'Pledge Detail'],
          ['/', undefined, '/pledges/active'],
        ),
      };
    }
    if (originPath === '/tasks') {
      return {
        title: 'Pledge Detail',
        breadcrumbs: buildBreadcrumbs(['Dashboard', 'FI Tasks', 'Pledge Detail'], ['/', '/tasks']),
      };
    }
    if (originPath === '/alerts') {
      return {
        title: 'Pledge Detail',
        breadcrumbs: buildBreadcrumbs(
          ['Dashboard', 'Operations', 'Compliance Alerts', 'Pledge Detail'],
          ['/', undefined, '/alerts'],
        ),
      };
    }
    return PAGE_INFO['/pledges/:pledgeId'];
  }
  if (PAGE_INFO[pathname]) {
    return PAGE_INFO[pathname];
  }
  // Special-case: /syndication/offerings/:offeringId/allocations is the
  // lead-side allocations review. The dynamic offeringId segment defeats
  // the standard prefix walker (which would resolve to the "Offering"
  // breadcrumb). Match the suffix explicitly.
  if (/^\/syndication\/offerings\/[^/]+\/allocations\/?$/.test(pathname)) {
    return {
      title: 'Allocations Review',
      breadcrumbs: buildBreadcrumbs(
        ['Dashboard', 'Loan Participations', 'Marketplace', 'Allocations'],
        ['/', undefined, '/syndication/marketplace', undefined],
      ),
    };
  }
  // Prefix match for dynamic routes
  const pathParts = pathname.split('/').filter(Boolean);
  while (pathParts.length > 0) {
    const testPath = '/' + pathParts.join('/');
    if (PAGE_INFO[testPath]) {
      return PAGE_INFO[testPath];
    }
    pathParts.pop();
  }
  return {
    title: 'Page',
    breadcrumbs: buildBreadcrumbs(['Dashboard', 'Page'], ['/']),
  };
}

// PLATFORM_ADMIN_GROUPS removed (Portal Separation Audit 2026-05-09).
// Platform admins at 9300 now see buildFiTenantGroups (apex FI operator
// view). System/platform config is canonical at platform-admin (9303).
// Routes previously gated behind PLATFORM_ADMIN_GROUPS remain declared
// in routes/index.tsx for back-compat but are not reachable from the nav.

// ---------------------------------------------------------------------------
// FI-tenant nav groups (Sprint C14 Lane A WP A-1)
//
// The admin-portal nav for FI-tenant operators is rebuilt from the
// loan-origination-flavored CU_ADMIN_GROUPS that landed pre-C14 to a
// 9-group structure aligned to the actual jobs-to-be-done on the Aaim
// platform: collateral evaluation + pledge management. LOS surfaces are
// preserved (sacrosanct future-partner work) but moved behind the
// `enableLosPreview` feature flag.
//
// Three groups are CONDITIONAL on tenant-scoped feature flags
// (Institution.enableLosPreview / .enablePeValuation /
// .enableLoanParticipations -- Loan Participations entitlement). The
// resolver below filters the array based
// on the flags returned from `useInstitutionFlags()`. Server-side gating
// (WP A-3) returns 404 for the corresponding API routes when the flag is
// OFF so deep links cannot leak surface existence.
//
// Permission gating on items continues to use the case-insensitive
// `requiredPermission` mechanism in `RoleBasedNav` (per
// `.claude/rules/authz-case-insensitive.md`); group-level `requiredRole`
// is intentionally absent because the resolver chooses the group set.
// ---------------------------------------------------------------------------

/**
 * Sprint C14 final FI-tenant nav -- 9 groups, two conditional (GH #676,
 * 2026-08-15: the former third conditional group, the standalone "PE
 * Valuation" group, was relocated into FI Configuration + Collateral
 * Monitoring; `flags.enablePeValuation` now gates items within those two
 * groups instead of a whole group). Receives the live `flags` so
 * conditional groups (and the item-level PE Valuation surfaces) self-elide
 * when the tenant has not contracted the corresponding surface.
 */
function buildFiTenantGroups(flags: InstitutionFlags): NavGroupDefinition[] {
  const groups: NavGroupDefinition[] = [
    // 1. Operations
    // nav-id: pledgeOperations
    {
      id: 'operations',
      label: 'Operations',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
        { id: 'pledges', label: 'Pledge Inbox', icon: InboxIcon },
        { id: 'pledges-active', label: 'Active Pledges', icon: ClipboardListIcon },
        { id: 'alerts', label: 'Compliance Alerts', icon: AlertTriangleIcon },
        { id: 'tasks', label: 'FI Tasks', icon: ClipboardListIcon },
      ],
    },
    // 1b. Collateral -- point-in-time collateral-evaluation surfaces (ATTOM-
    // backed property valuation, title search, lien verification, property
    // profile). Always-visible: these are core collateral-evaluation surfaces
    // (the Aaim USP), not loan origination. Moved out of the enableLosPreview
    // "Lending (Preview)" group 2026-06-19 (nav-tree audit) -- they were
    // unreachable for every role under default flags. Mirrors the always-visible
    // Lien Filings group that houses UCC Filings.
    // nav-id: collateral
    {
      id: 'collateral',
      label: 'Collateral',
      requiredPermission: 'collateral:read',
      items: [
        { id: 'collateral-property-valuations', label: 'Property Valuations', icon: HomeIcon },
        { id: 'collateral-title-searches', label: 'Title Searches', icon: MapPinIcon },
        { id: 'collateral-lien-verifications', label: 'Lien Verifications', icon: ShieldCheckIcon },
        { id: 'collateral-property-profiles', label: 'Property Profile', icon: DatabaseIcon },
      ],
    },
    // 2. Collateral Monitoring
    // nav-id: collateralMonitoringNav
    {
      id: 'collateral-monitoring',
      label: 'Collateral Monitoring',
      requiredPermission: 'collateral:read',
      items: [
        { id: 'monitoring', label: 'Health Overview', icon: HeartPulseIcon },
        { id: 'monitoring-classification', label: 'Asset Classification', icon: TagIcon },
        { id: 'monitoring-valuation-history', label: 'Valuation History', icon: HistoryIcon },
        { id: 'monitoring-concentration', label: 'Concentration Risk', icon: BarChart3Icon },
        { id: 'monitoring-custodian-status', label: 'Custodian Status', icon: ServerIcon },
        { id: 'admin-webhook-deliveries', label: 'Webhook Delivery Log', icon: ActivityIcon },
        // PE Collateral Monitoring (relocated from the standalone "PE
        // Valuation" group, GH #676 / 2026-08-15 operator ruling: relocate
        // per the prior rationale rather than substitute flag-gating for
        // relocation). Still partner-runway scaffolding per
        // feedback_los_is_future_partner_work.md -- dark until an FI signs a
        // PE-collateral partnership. Route-level gate unchanged
        // (routes/index.tsx PeFeatureGate). Role-gated to the roles the PE
        // mounts admit (lib/pe-roles.ts, GH #4228): this builder also serves
        // the resolver's default branch, so auditor and partner reach it.
        // flag-gate: enablePeValuation
        ...(flags.enablePeValuation
          ? [
              {
                id: 'pe-collateral-monitoring',
                label: 'PE Collateral Monitoring',
                icon: LayersIcon,
                requiredRole: PE_USER_ROLES,
              },
            ]
          : []),
      ],
    },
    // 3. Lien Filings (renamed from "Lien Perfection" 2026-05-17, Sprint B Lane B2)
    // Default leaf is "Filings Awaiting Review": auto-populated index of UCC
    // filings produced by the FI pledge approval flow that still need
    // operator review before submission. The manual "Our Filings" view
    // (UCCFilings page) is retained for amendments and continuations.
    // nav-id: lienPerfection
    {
      id: 'lien-perfection',
      label: 'Lien Filings',
      items: [
        { id: 'lien-awaiting-review', label: 'Filings Awaiting Review', icon: InboxIcon },
        { id: 'collateral-ucc-filings', label: 'UCC Filings', icon: FileSearchIcon },
        { id: 'lien-filing-status', label: 'Filing Status', icon: FileTextIcon },
        { id: 'lien-jurisdiction-reports', label: 'Jurisdiction Reports', icon: MapPinIcon },
        { id: 'lien-fee-reconciliation', label: 'Fee Reconciliation', icon: CreditCardIcon },
      ],
    },
  ];

  // 4. Loan Participations (conditional). Regulator-canonical term for
  // syndication. The legacy "Liquidity Network" label was Aaim-internal jargon
  // that did not match how examiners read the FI's risk book. Flag name kept
  // as-is for backward compat; label rewritten per regulator vocabulary.
  if (flags.enableLoanParticipations) {
    // nav-id: liquidityNetwork
    // flag-gate: enableLoanParticipations
    groups.push({
      id: 'liquidity-network',
      label: 'Loan Participations',
      items: [
        { id: 'syndication-marketplace', label: 'Marketplace', icon: StoreIcon },
        { id: 'syndication-list-a-loan', label: 'List a Loan', icon: TagIcon },
        { id: 'syndication-indications', label: 'Indications of Interest', icon: BookmarkIcon },
        { id: 'syndication-commitments', label: 'Commitments', icon: ArrowLeftRightIcon },
        { id: 'syndication-positions', label: 'Positions', icon: PieChartIcon },
        { id: 'syndication-risk-capital', label: 'Risk Capital', icon: TargetIcon },
      ],
    });
  }

  // 4a. Aaim Tempo (conditional). The FI's overdraft-coverage product console,
  // promoted to a top-level product group per operator ruling 2026-08-27.
  // Gated on enableOverdraftPreview. Visibility preserved at settings:update
  // (FI_ADMIN), matching its prior FI-Configuration placement. Display brand
  // per terminology/terms/member-liquidity.yaml. The visible route is /tempo and
  // the API path is /api/v1/tempo/*, both renamed off the retiring nomenclature
  // per operator ruling 2026-08-27. The tempo-engine service name and
  // the spec leaf filename are unchanged (operator-gated).
  if (flags.enableOverdraftPreview) {
    // nav-id: aaimTempoNav
    // flag-gate: enableOverdraftPreview
    groups.push({
      id: 'aaim-tempo',
      label: 'Aaim Tempo',
      requiredPermission: 'settings:update',
      items: [
        { id: 'policy-member-liquidity', label: 'Coverage Policy', icon: WalletIcon },
        { id: 'policy-pool-appetite', label: 'Pool Appetite Policy', icon: PieChartIcon },
        // GH #2910 deliverable 4: the per-account decision screen gets its own
        // entry. Tempo declares no account-list operation, so the page opens on
        // a lookup field.
        { id: 'tempo-accounts', label: 'Account Decision', icon: FileSearchIcon },
      ],
    });
  }

  // 4b. Economic Modeling (conditional). Scenario projections over the FI's
  // own book. Gated on enableOverdraftPreview -- ONE flag, TWO programs, by
  // operator ruling 2026-08-26 (GO-LIVE-PLAN section 12.5 C2; the LOCKED
  // semantics block in platform-admin.openapi.yaml carries the widened
  // meaning). The flag's name does not describe this surface, which is the
  // stated cost of not minting a sixth flag. GH #2727.
  //
  // The label read "Aaim Foresight" until 2026-09-21. Operator ruling on
  // GH #2915: this lab takes its own name, and `Foresight` is reserved for
  // depositor-level decisioning (docs/architecture/tempo-design.md 12.2,
  // the Tempo/Foresight namespace ruling). The label now matches the C4
  // component name `economicModelingNav` and the nav group id below, which
  // both already read Economic Modeling. Routes, flag and entitlements are
  // unchanged -- GH #2734 owns the flag question.
  if (flags.enableOverdraftPreview) {
    // nav-id: economicModelingNav
    // flag-gate: enableOverdraftPreview
    groups.push({
      id: 'economic-modeling',
      label: 'Economic Modeling',
      items: [
        { id: 'modeling-scenarios', label: 'Economic Modeling', icon: PieChartIcon },
      ],
    });
  }

  // 5. Compliance -- the FI's view of the risk book through audit and
  // regulatory lenses. Scoped in practice to collateral; primary
  // BSA/OFAC/Fair Lending/Vendor Management compliance lives in the FI's
  // existing system per Sprint D banking-06. The "Collateral" qualifier
  // belongs in doctrine, not in screen real estate -- the operator is
  // already inside Aaim.
  // nav-id: auditCompliance
  groups.push({
    id: 'audit-compliance',
    label: 'Compliance',
    items: [
      { id: 'audit-pledges', label: 'Pledge Audit Trail', icon: HistoryIcon, requiredPermission: 'audit:read' },
      { id: 'compliance', label: 'Compliance Reports', icon: ShieldIcon, requiredPermission: 'compliance:read' },
      // fi-nav-label-parity (operator ruling), under the GH #2847 role-asymmetry
      // review. The FI_ADMIN Dashboard tile opens /dashboards/compliance-status,
      // and with no item in this tree the sidebar highlighted nothing there.
      // Same shape as the fi_compliance item: ComplianceStatus.tsx reads
      // /api/admin/compliance/dashboard/*, which platform-admin-api mounts
      // behind requireAuthenticatedBearer + requireTenantScope with no
      // per-route permission gate, so this is not the GH #1930 guaranteed-403
      // shape.
      { id: 'dashboards-compliance-status', label: 'Compliance Status', icon: GaugeIcon },
      { id: 'dashboards-risk', label: 'Risk Dashboard', icon: TrendingUpIcon },
      { id: 'audit-regulatory-readiness', label: 'Regulatory Readiness', icon: ShieldCheckIcon },
      // GH #1930: examiner evidence package (manifest preview + ZIP download).
      //
      // GH #678 (operator ruling 2026-10-21, confirmed 2026-10-23: "Examiner
      // evidence is for tenants to prove compliance"): the platform-admin-api
      // evidence-package endpoints admit fi_admin and fi_compliance through
      // requireEvidencePackageAccess, bound to the caller's own tenant claim,
      // beside the platform-admin roles they always admitted. Before the
      // ruling the item was role-gated to the platform-admin set alone, since
      // FI_ADMIN got a guaranteed 403.
      //
      // This builder is shared by the FI_ADMIN persona AND, via the resolver's
      // default branch, apex platform admins operating 9300, so the item stays
      // role-gated to exactly the roles the backend admits (the role list in
      // pages/audit/ExaminerEvidence.tsx EVIDENCE_PACKAGE_ROLES, less
      // examiner, which this nav never offered). Item-level requiredRole is
      // honored by RoleBasedNav.hasAccess (case-insensitive, ANY-of).
      {
        id: 'audit-examiner-evidence',
        label: 'Examiner Evidence',
        icon: ArchiveIcon,
        requiredRole: [...PLATFORM_ADMIN_ROLES, 'fi_admin', 'fi_compliance'],
      },
    ],
  });

  // 6. FI Configuration -- the FI's risk-control levers. Per project_aaim_usp,
  // these surfaces frame the FI as the term-setter; Aaim provides calibrated
  // baselines and the perfection framework.
  // nav-id: configuration
  groups.push({
    id: 'configuration',
    label: 'FI Configuration',
    // Sprint D-followup (2026-05-17): the prior `configuration:write` literal
    // was a phantom permission name -- it never existed in the canonical
    // aaim-core seed-rbac.ts. The actual FI_ADMIN-owned permission for
    // policy-author surfaces is `settings:update`; FI_LOAN_OFFICER and
    // FI_COMPLIANCE do not own it, which is the correct gate for the FI
    // Configuration group (FI_ADMIN-only access). Caught during the
    // FI_ADMIN puppeteer walkthrough on 2026-05-17 -- the group was
    // invisible under any FI tenant login because the literal didn't match
    // any seeded permission.
    requiredPermission: 'settings:update',
    items: [
      { id: 'admin-config-organization', label: 'Organization Profile', icon: BuildingIcon },
      { id: 'admin-config-webhook-endpoints', label: 'Webhook Endpoints', icon: WebhookIcon },
      { id: 'policy-cha-thresholds', label: 'Advance Rate Programs', icon: TrendingUpIcon },
      { id: 'policy-concentration', label: 'Concentration Limits', icon: BarChart3Icon },
      { id: 'policy-asset-classes', label: 'Asset Class Eligibility', icon: LayersIcon },
      // GH #1729 deliverable 2(c), 2026-09-15. /policy/monitoring and
      // /policy/audit are two of the five tabs CollateralPolicyPage mounts
      // under `policy/*` (pages/collateral-policy/CollateralPolicyPage.tsx),
      // and the other three are the three items directly above. They were in
      // SEARCHABLE_PAGES and in NAV_ID_TO_PATH but in no nav group, so the
      // policy editor's own Monitoring & Alerts and Audit Trail tabs were
      // palette-only. This group is the Collateral Policy group: it is where
      // the sibling policy leaves live.
      { id: 'policy-monitoring', label: 'Monitoring & Alerts', icon: ActivityIcon },
      { id: 'policy-audit', label: 'Policy Audit Trail', icon: HistoryIcon },
      // PE Fund Administration + PE Configuration (relocated from the
      // standalone "PE Valuation" group, GH #676 / 2026-08-15 operator
      // ruling: relocate per the prior rationale rather than substitute
      // flag-gating for relocation). Fund Administration is PE fund
      // reference-data CRUD (fund metadata, manager mapping, LP interests,
      // capital activity); PE Configuration is the FI's policy on PE
      // haircuts and eligible managers -- both are FI-set administrative
      // surfaces, consistent with the rest of this group. Still
      // partner-runway scaffolding per feedback_los_is_future_partner_work.md
      // -- dark until an FI signs a PE-collateral partnership. Route-level
      // gate unchanged (routes/index.tsx PeFeatureGate). Both items carry the
      // PE role gate (lib/pe-roles.ts, GH #4228), as PE Collateral Monitoring
      // does.
      // flag-gate: enablePeValuation
      ...(flags.enablePeValuation
        ? [
            { id: 'pe-funds', label: 'PE Fund Administration', icon: WalletIcon, requiredRole: PE_USER_ROLES },
            { id: 'pe-configuration', label: 'PE Configuration', icon: SettingsIcon, requiredRole: PE_USER_ROLES },
          ]
        : []),
      // Aaim Tempo policy consoles (Coverage Policy + Pool Appetite) were
      // promoted out of FI Configuration to the top-level "Aaim Tempo" product
      // group (operator ruling 2026-08-27; see the aaimTempoNav block above).
      // Workflow ordering -- ruling D3 (2026-08-17), reaffirming ruling 3 of
      // 2026-08-13. The FI admin SELECTS a pledge workflow ordering from the
      // subset an Aaim operator has allowlisted for this tenant; authoring a
      // recipe and editing that allowlist are Aaim-operator acts on
      // platform.aaim.com : 9303 and are absent from this portal. Sits beside
      // Custodian Connections because the ordering's terminal act is the
      // binding control instruction to the custodian. No flag gate: the
      // surface is honest in its zero-alternative state, which is today's
      // normal state.
      { id: 'admin-config-workflow-ordering', label: 'Workflow Ordering', icon: ClipboardListIcon },
      // SLA settings (GH #2390). The age thresholds the Tasks queue labels
      // Within SLA / Approaching / Over SLA against, for this institution.
      // The group's settings:update is the permission the save requires.
      { id: 'admin-config-sla', label: 'SLA Settings', icon: ClockIcon },
      // Lending configuration (GH #4688), the institution's own lending
      // parameters. The arc ruling of 2026-08-20 places it in this portal;
      // lending-platform admits the save for fi_admin and the
      // platform-administrator roles only.
      { id: 'admin-config-lending', label: 'Lending Configuration', icon: CreditCardIcon },
      { id: 'admin-config-custodian-connections', label: 'Custodian Connections', icon: LinkIcon },
      { id: 'admin-config-branding', label: 'Consumer Portal Branding', icon: ImageIcon },
      { id: 'admin-config-landing-copy', label: 'Landing Page Copy', icon: ImageIcon },
    ],
  });

  // 7. Administration
  //
  // Sprint 5 (FI Staff Authn/Z) -- three surfaces appended to the
  // Administration group:
  //
  //   1. Pending IdP Claims (badge slot wired in `navGroups` memo below;
  //      count comes from useQuery on
  //      GET /api/v1/admin/idp-bindings/{bindingId}/pending-claims
  //      filtered to status=PENDING per Sprint 1 deviation #2 -- the
  //      backend exposes this as `resolvedAt IS NULL` rows; staleTime
  //      30s so the FI Admin's nav reflects post-sign-in queue depth
  //      within one round-trip).
  //   2. IdP Role Mappings  (claim-string -> Aaim Role admin surface).
  //   3. ABAC Policies      (per-tenant policy CRUD + role attach).
  //
  // All three share the group-level `roles:manage` requiredPermission.
  // FI Admin (fi_admin) carries this permission post-Sprint 1; FI Loan
  // Officer / FI Compliance do not, and the group self-hides for them
  // via RoleBasedNav.
  // nav-id: administration
  groups.push({
    id: 'administration',
    label: 'Administration',
    requiredPermission: 'roles:manage',
    items: [
      { id: 'admin-users', label: 'User Management', icon: UsersIcon },
      { id: 'admin-idp-pending-claims', label: 'Pending IdP Claims', icon: InboxIcon },
      { id: 'admin-idp-role-mappings', label: 'IdP Role Mappings', icon: TagIcon },
      { id: 'admin-abac-policies', label: 'ABAC Policies', icon: FileTextIcon },
      { id: 'admin-api-keys', label: 'API Keys', icon: KeyIcon },
      // Examiner provisioning (Sprint 174 D-003). Added to nav 2026-06-19
      // (nav-tree audit C2) -- the route existed but had no nav entry for any role.
      { id: 'admin-examiners', label: 'Examiners', icon: ShieldCheckIcon },
      // Support cases (GH #1729 deliverable 2(a), 2026-09-15). The support
      // pages gate on ROLES, not on a permission literal -- SupportCases.tsx
      // and SupportCaseDetail.tsx admit FI_OPERATOR_ROLES (fi_admin,
      // fi_loan_officer, fi_compliance), so there is no page-checked
      // permission to mirror here. Per the brief's fallback it inherits this
      // group's `roles:manage`, which fi_admin carries. Apex admins reach this
      // builder through the resolver's default branch; the item opens the
      // operator-console pointer card for them, since GH #678 (operator ruling
      // 2026-10-23) moved the operator queue, SLA rules and agent roster to
      // platform-admin.
      { id: 'admin-support', label: 'Support Cases', icon: LifeBuoyIcon },
    ],
  });

  // 8. Lending (Preview) (conditional). Flag default: false. This surface
  // does NOT advance the 10-step critical path -- the FI's loan-origination
  // system (LOS) is the canonical owner of loan applications, underwriting,
  // and origination. Aaim is a collateral evaluation and pledge management
  // module that integrates into the FI's existing LOS, not a replacement
  // for it (see CLAUDE.md product-definition mandate). Pages preserved as
  // partner-runway scaffolding per feedback_los_is_future_partner_work.md
  // for FIs that sign an LOS-integration partnership; the surface stays
  // dark by default.
  if (flags.enableLosPreview) {
    // nav-id: lendingPreview
    // flag-gate: enableLosPreview
    groups.push({
      id: 'lending-preview',
      label: 'Lending (Preview)',
      items: [
        { id: 'dashboards-loan-pipeline', label: 'Loan Pipeline', icon: FilterIcon },
        { id: 'loans-active', label: 'Active Loans', icon: GaugeIcon },
        { id: 'portfolio', label: 'Portfolio', icon: TrendingUpIcon },
        { id: 'members', label: 'Customer Lookup', icon: UserIcon, requiredPermission: 'members:read' },
        // Signatures stays here (hidden by default). Members sign pledge
        // contracts in-house (single signer, FULLY_EXECUTED, observed on prod
        // 2026-09-15); what is missing is an FI-facing list of executed
        // instruments, so the page states that absence.
        // The four deployed collateral surfaces moved to the always-visible
        // Collateral group above (2026-06-19 nav-tree audit).
        { id: 'collateral-signatures', label: 'Signatures', icon: FileTextIcon },
      ],
    });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Group forms shared by the non-admin builders (GH #2847)
//
// The fi_admin builder above keeps its own literals: platform admins reach it
// through the resolver's default branch, and its reach is pinned unchanged by
// PortalLayout.nav-reach.test.tsx. The forms below are the read-posture groups
// the loan officer, compliance, viewer and treasury builders share, so a
// change to one of those surfaces moves every role that carries it at once.
// ---------------------------------------------------------------------------

/** Operations, the work-queue entry points every FI persona's nav opens on. */
function operationsGroup(): NavGroupDefinition {
  return {
    id: 'operations',
    label: 'Operations',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
      { id: 'pledges', label: 'Pledge Inbox', icon: InboxIcon },
      { id: 'pledges-active', label: 'Active Pledges', icon: ClipboardListIcon },
      { id: 'alerts', label: 'Compliance Alerts', icon: AlertTriangleIcon },
      // Sprint D banking-08: compliance receives FI Tasks routed to this
      // operator (e.g., consent-proof exceptions, regulatory-readiness
      // remediations).
      { id: 'tasks', label: 'FI Tasks', icon: ClipboardListIcon },
    ],
  };
}

/**
 * Collateral (read posture) -- the deployed collateral-evaluation surfaces.
 * Moved out of Lending(Preview) 2026-06-19 (nav-tree audit).
 */
function collateralGroup(): NavGroupDefinition {
  return {
    id: 'collateral',
    label: 'Collateral',
    items: [
      { id: 'collateral-property-valuations', label: 'Property Valuations', icon: HomeIcon },
      { id: 'collateral-title-searches', label: 'Title Searches', icon: MapPinIcon },
      { id: 'collateral-lien-verifications', label: 'Lien Verifications', icon: ShieldCheckIcon },
      { id: 'collateral-property-profiles', label: 'Property Profile', icon: DatabaseIcon },
    ],
  };
}

/**
 * Collateral Monitoring (read posture). Gated on `collateral:read`, the gate
 * the fi_admin builder's group carries. The fi_admin form adds the Webhook
 * Delivery Log and, under enablePeValuation, PE Collateral Monitoring; neither
 * is part of the read posture.
 */
function collateralMonitoringReadGroup(): NavGroupDefinition {
  return {
    id: 'collateral-monitoring',
    label: 'Collateral Monitoring',
    requiredPermission: 'collateral:read',
    items: [
      { id: 'monitoring', label: 'Health Overview', icon: HeartPulseIcon },
      { id: 'monitoring-classification', label: 'Asset Classification', icon: TagIcon },
      { id: 'monitoring-valuation-history', label: 'Valuation History', icon: HistoryIcon },
      { id: 'monitoring-concentration', label: 'Concentration Risk', icon: BarChart3Icon },
      { id: 'monitoring-custodian-status', label: 'Custodian Status', icon: ServerIcon },
    ],
  };
}

/**
 * Lien Filings (read posture). Sprint D banking-02: read-only lien-perfection
 * visibility. Write actions remain gated to FI_ADMIN at the page level; nav
 * exposes read access so the operator can verify a file status without
 * bouncing through compliance. The two lien report items (Jurisdiction
 * Reports, Fee Reconciliation) stay with fi_admin and fi_compliance.
 */
function lienFilingsReadGroup(): NavGroupDefinition {
  return {
    id: 'lien-perfection-readonly',
    label: 'Lien Filings',
    items: [
      { id: 'lien-awaiting-review', label: 'Filings Awaiting Review', icon: InboxIcon },
      { id: 'lien-filing-status', label: 'Filing Status', icon: FileTextIcon },
      { id: 'collateral-ucc-filings', label: 'UCC Filings', icon: FileSearchIcon },
    ],
  };
}

/**
 * Loan Participations, item-gated (GH #2847, operator ruling 2026-10-22).
 * The same six items as the fi_admin builder's group, each gated on the
 * catalog permission (rbac-catalog.ts) for the act its page performs:
 *
 *   Marketplace, Commitments, Positions  participation:read   (reads; the
 *       Commitments page reads the commitment books of the caller's own
 *       listings)
 *   List a Loan                          participation:create (creates an
 *       offering from an originated loan)
 *   Indications of Interest              participation:commit (the
 *       participant-side IOI dashboard and its allocation accept/decline)
 *   Risk Capital                         participation:commit OR
 *       participation:manage (the CRT subscription that funds the
 *       participation book)
 *
 * Gated on enableLoanParticipations like the fi_admin group. Visibility only:
 * loan-syndication-protocol keeps its own role and participant gates.
 */
function loanParticipationsGroup(flags: InstitutionFlags): NavGroupDefinition[] {
  if (!flags.enableLoanParticipations) return [];
  return [
    {
      id: 'liquidity-network',
      label: 'Loan Participations',
      items: [
        { id: 'syndication-marketplace', label: 'Marketplace', icon: StoreIcon, requiredPermission: 'participation:read' },
        { id: 'syndication-list-a-loan', label: 'List a Loan', icon: TagIcon, requiredPermission: 'participation:create' },
        { id: 'syndication-indications', label: 'Indications of Interest', icon: BookmarkIcon, requiredPermission: 'participation:commit' },
        { id: 'syndication-commitments', label: 'Commitments', icon: ArrowLeftRightIcon, requiredPermission: 'participation:read' },
        { id: 'syndication-positions', label: 'Positions', icon: PieChartIcon, requiredPermission: 'participation:read' },
        {
          id: 'syndication-risk-capital',
          label: 'Risk Capital',
          icon: TargetIcon,
          requiredPermission: ['participation:commit', 'participation:manage'],
        },
      ],
    },
  ];
}

/**
 * Lending (Preview), read posture, under enableLosPreview. Same items as the
 * fi_admin form: Customer Lookup keeps its `members:read` gate.
 */
function lendingPreviewGroup(flags: InstitutionFlags): NavGroupDefinition[] {
  if (!flags.enableLosPreview) return [];
  return [
    {
      id: 'lending-preview',
      label: 'Lending (Preview)',
      items: [
        { id: 'dashboards-loan-pipeline', label: 'Loan Pipeline', icon: FilterIcon },
        { id: 'loans-active', label: 'Active Loans', icon: GaugeIcon },
        { id: 'portfolio', label: 'Portfolio', icon: TrendingUpIcon },
        { id: 'members', label: 'Customer Lookup', icon: UserIcon, requiredPermission: 'members:read' },
        // Signatures stays here (hidden by default). Members sign pledge
        // contracts in-house (single signer, FULLY_EXECUTED, observed on prod
        // 2026-09-15); what is missing is an FI-facing list of executed
        // instruments.
        { id: 'collateral-signatures', label: 'Signatures', icon: FileTextIcon },
      ],
    },
  ];
}

/**
 * FI_LOAN_OFFICER subset of the FI-tenant nav. Operations + Collateral +
 * Collateral Monitoring (read posture) + Lien Filings (read-only) + Loan
 * Participations (item-gated; GH #2847) + Lending (Preview) when enabled. No
 * Compliance group, and one Configuration item, Lending Configuration, read
 * only (least-privilege, operator ruling 2026-10-22, widened by that one item
 * on 2026-10-29 for GH #4688).
 */
function buildLoanOfficerGroups(flags: InstitutionFlags): NavGroupDefinition[] {
  return [
    operationsGroup(),
    collateralGroup(),
    collateralMonitoringReadGroup(),
    lienFilingsReadGroup(),
    ...loanParticipationsGroup(flags),
    // FI Configuration, one item (Rob's ruling 2026-10-29 on GH #4688): the
    // loan officer reads the institution's lending configuration and saves
    // nothing. GET /api/v1/lending-config admits any session of the tenant;
    // the PUT stays with fi_admin and the platform-administrator roles
    // (lending-platform requireLendingConfigWriter), and the page shows this
    // role the values with no form. No other Configuration item: the
    // fi_admin group's settings:update gate is not borrowed, since this
    // persona does not carry it.
    {
      id: 'configuration',
      label: 'FI Configuration',
      items: [
        { id: 'admin-config-lending', label: 'Lending Configuration', icon: CreditCardIcon },
      ],
    },
    // Administration (GH #1729 deliverable 2(a), 2026-09-15). This builder had
    // no Administration group; it gets one holding Support Cases alone, because
    // SupportCases.tsx / SupportCaseDetail.tsx / SupportNewCase.tsx admit
    // fi_loan_officer by role (FI_OPERATOR_ROLES) and the loan officer had no
    // way to file or read a case from inside the portal. Ungated, consistent
    // with every other group in this builder -- this persona does not carry
    // `roles:manage`, so borrowing the fi_admin group's permission would hide
    // the item from the persona the pages admit.
    {
      id: 'administration',
      label: 'Administration',
      items: [
        { id: 'admin-support', label: 'Support Cases', icon: LifeBuoyIcon },
      ],
    },
    // GH #678. buildGroupsForRoles picks this builder for any session holding
    // fi_loan_officer, so a loan officer who also holds fi_compliance lands
    // here, and the evidence-package endpoints admit that session. The group
    // is gated on fi_compliance, so a loan officer without it sees nothing.
    {
      id: 'audit-compliance',
      label: 'Compliance',
      requiredRole: 'fi_compliance',
      items: [
        { id: 'audit-examiner-evidence', label: 'Examiner Evidence', icon: ArchiveIcon },
      ],
    },
    ...lendingPreviewGroup(flags),
  ];
}

/**
 * CU_COMPLIANCE subset of the FI-tenant nav. Operations (read-only via
 * downstream RBAC) + Collateral + Collateral Monitoring (read posture; GH
 * #2847) + Audit & Compliance (full) + Lien Filings + Loan Participations
 * (item-gated, so the compliance officer's `participation:read` opens the
 * read items only; GH #2847). No Configuration access.
 */
function buildComplianceGroups(flags: InstitutionFlags): NavGroupDefinition[] {
  return [
    operationsGroup(),
    collateralGroup(),
    collateralMonitoringReadGroup(),
    {
      id: 'audit-compliance',
      label: 'Compliance',
      items: [
        { id: 'audit-pledges', label: 'Pledge Audit Trail', icon: HistoryIcon },
        { id: 'compliance', label: 'Compliance Reports', icon: ShieldIcon },
        // GH #1729 deliverable 2(e), 2026-09-15. /dashboards/compliance-status
        // carried a NAV_ID_TO_PATH entry and a PAGE_INFO breadcrumb but sat in
        // no nav group, so its only in-app entry point was the Dashboard tile
        // (pages/Dashboard.tsx). KEPT rather than pruned, and given to this
        // persona: the page renders for fi_compliance -- ComplianceStatus.tsx
        // reads /api/admin/compliance/dashboard/*, which platform-admin-api
        // mounts behind requireAuthenticatedBearer + requireTenantScope with NO
        // per-route permission gate (services/platform-admin-api/src/
        // routes/compliance.routes.ts), so this is not the GH #1930
        // guaranteed-403 shape.
        { id: 'dashboards-compliance-status', label: 'Compliance Status', icon: GaugeIcon },
        { id: 'dashboards-risk', label: 'Risk Dashboard', icon: TrendingUpIcon },
        { id: 'audit-regulatory-readiness', label: 'Regulatory Readiness', icon: ShieldCheckIcon },
        // GH #678 (operator ruling 2026-10-21, confirmed 2026-10-23: "Examiner
        // evidence is for tenants to prove compliance"). The platform-admin-api
        // evidence-package endpoints admit fi_compliance through
        // requireEvidencePackageAccess, bound to the caller's own tenant claim,
        // so the compliance officer gets the item. Before the ruling it was
        // withheld here because FI_COMPLIANCE got a guaranteed 403 (GH #1930).
        // Ungated, consistent with the rest of this builder, which only an
        // fi_compliance holder reaches.
        { id: 'audit-examiner-evidence', label: 'Examiner Evidence', icon: ArchiveIcon },
        // 'admin-audit' (Audit Logs -> /admin/audit) removed 2026-06-19: the
        // audit-logs surface moved to platform-admin (F-PORTAL-AUDIENCE-SPLIT);
        // admin-portal has no /admin/audit route, so the item dead-ended on
        // NotFoundPage for every fi_compliance operator (nav-tree audit H2).
      ],
    },
    {
      id: 'lien-perfection',
      label: 'Lien Filings',
      items: [
        { id: 'lien-awaiting-review', label: 'Filings Awaiting Review', icon: InboxIcon },
        { id: 'collateral-ucc-filings', label: 'UCC Filings', icon: FileSearchIcon },
        { id: 'lien-filing-status', label: 'Filing Status', icon: FileTextIcon },
        { id: 'lien-jurisdiction-reports', label: 'Jurisdiction Reports', icon: MapPinIcon },
        { id: 'lien-fee-reconciliation', label: 'Fee Reconciliation', icon: CreditCardIcon },
      ],
    },
    ...loanParticipationsGroup(flags),
    // Administration (GH #1729 deliverable 2(a), 2026-09-15). Same reasoning as
    // the loan-officer builder: the support pages admit fi_compliance by role,
    // and this builder had no Administration group to hang the item on.
    // Ungated, consistent with the rest of this builder.
    {
      id: 'administration',
      label: 'Administration',
      items: [
        { id: 'admin-support', label: 'Support Cases', icon: LifeBuoyIcon },
      ],
    },
  ];
}

/**
 * FI_VIEWER nav (GH #2847, operator ruling 2026-10-22). This role took the
 * FI_ADMIN builder through the default branch, which opened 36 paths to a
 * read-only role against the loan officer's 23. Derived from its catalog set
 * (rbac-catalog.ts FI_VIEWER: loans, collateral, members, reports and
 * participation, all :read): the read-posture groups that need
 * `collateral:read`, and Lending (Preview) under its flag.
 *
 * Withheld, and why:
 *   - Loan Participations: every loan-syndication-protocol route runs
 *     requireUserRole(FI_USER_ROLES), and that list has no fi_viewer
 *     (services/loan-syndication-protocol/src/routes/syndication.routes.ts).
 *     The catalog's participation:read would open a guaranteed 403, the GH
 *     #1930 shape.
 *   - Compliance: the role holds no audit:* or compliance:* permission.
 *   - Economic Modeling, Aaim Tempo, FI Configuration, Administration: no
 *     non-admin builder carries them, and the last three need
 *     settings:update or roles:manage. The support pages admit
 *     FI_OPERATOR_ROLES only.
 */
function buildViewerGroups(flags: InstitutionFlags): NavGroupDefinition[] {
  return [
    operationsGroup(),
    collateralGroup(),
    collateralMonitoringReadGroup(),
    lienFilingsReadGroup(),
    ...lendingPreviewGroup(flags),
  ];
}

/**
 * FI_TREASURY nav (GH #2847, operator ruling 2026-10-22), the buy-side
 * participation desk. Took the FI_ADMIN builder through the default branch
 * before. Derived from its catalog set (rbac-catalog.ts FI_TREASURY:
 * participation:read and :commit, loans, collateral, compliance and reports
 * :read): the viewer's read posture, Loan Participations item-gated (its
 * :commit opens Indications of Interest and Risk Capital; it holds no
 * :create, so List a Loan stays closed), and Compliance Reports alone for its
 * compliance:read diligence read. Lending (Preview) keeps Customer Lookup
 * behind members:read, which this role does not hold.
 */
function buildTreasuryGroups(flags: InstitutionFlags): NavGroupDefinition[] {
  return [
    operationsGroup(),
    collateralGroup(),
    collateralMonitoringReadGroup(),
    lienFilingsReadGroup(),
    ...loanParticipationsGroup(flags),
    {
      id: 'audit-compliance',
      label: 'Compliance',
      items: [
        { id: 'compliance', label: 'Compliance Reports', icon: ShieldIcon, requiredPermission: 'compliance:read' },
      ],
    },
    ...lendingPreviewGroup(flags),
  ];
}

/**
 * PLEDGE_REVIEWER nav (GH #2318, option A2, Robert's ruling 2026-10-23), the
 * staffer who works the step-8 Pledge Inbox. Took the FI_ADMIN builder
 * through the default branch before. Derived from its catalog set
 * (rbac-management.openapi.yaml x-system-role-catalog PLEDGE_REVIEWER:
 * pledge:read:inbox, pledge:accept, pledge:decline, lending-terms:commit):
 * Operations, the work queue. platform-admin-api serves Pledge Inbox and
 * Active Pledges (/api/admin/pledges) and FI Tasks (/api/admin/tasks) behind
 * pledge:read:inbox, and the accept and decline acts on the pledge detail
 * behind pledge:accept and pledge:decline. Compliance Alerts reads
 * /api/admin/compliance/dashboard/alerts, which carries no per-route
 * permission gate, as it does for every other FI builder.
 *
 * Withheld, and why:
 *   - Collateral and Collateral Monitoring: the role holds no collateral:*
 *     permission.
 *   - Lien Filings: post-approval filing work belongs to the FI admin and
 *     compliance builders; the role's set ends at the accept or decline.
 *   - Loan Participations: every loan-syndication-protocol route runs
 *     requireUserRole(FI_USER_ROLES), which has no pledge_reviewer.
 *   - Compliance: no audit:* or compliance:* permission.
 *   - Economic Modeling, Aaim Tempo, FI Configuration, Administration: they
 *     need settings:update or roles:manage, and the support pages admit
 *     FI_OPERATOR_ROLES only.
 */
function buildPledgeReviewerGroups(): NavGroupDefinition[] {
  return [operationsGroup()];
}

/**
 * PLEDGOR_ADVISOR nav (GH #2318, option A2, Robert's ruling 2026-10-23), the
 * advisor who enters and attests a consenting client's positions. Took the
 * FI_ADMIN builder through the default branch before. Its catalog set
 * (position:enter, position:attest, connection:read:pending,
 * pledge:read:own-tenant) is served by advisor-tools-api for the advisor-tools
 * SPA, which holds the attestation queue; no admin-portal route admits it on
 * those strings. In this portal the role reaches the three PE pages, whose
 * pe-valuation mounts admit it by role (lib/pe-roles.ts PE_USER_ROLES, GH
 * #2657), so they are its one group, under enablePeValuation like every PE
 * item. Dashboard is the landing page every FI builder opens on.
 *
 * Withheld: Pledge Inbox, Active Pledges and FI Tasks need pledge:read:inbox,
 * which the role does not hold; every other group needs a collateral:*,
 * compliance:*, audit:*, settings:update or roles:manage permission or an FI
 * operator role.
 */
function buildPledgorAdvisorGroups(flags: InstitutionFlags): NavGroupDefinition[] {
  const groups: NavGroupDefinition[] = [
    {
      id: 'operations',
      label: 'Operations',
      items: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon }],
    },
  ];
  // flag-gate: enablePeValuation
  if (flags.enablePeValuation) {
    groups.push({
      id: 'pe-valuation',
      label: 'PE Valuation',
      items: [
        { id: 'pe-funds', label: 'PE Fund Administration', icon: WalletIcon, requiredRole: PE_USER_ROLES },
        { id: 'pe-collateral-monitoring', label: 'PE Collateral Monitoring', icon: LayersIcon, requiredRole: PE_USER_ROLES },
        { id: 'pe-configuration', label: 'PE Configuration', icon: SettingsIcon, requiredRole: PE_USER_ROLES },
      ],
    });
  }
  return groups;
}

// ---------------------------------------------------------------------------
// PortalLayout Component
// ---------------------------------------------------------------------------

/**
 * Role -> nav-builder selection. Extracted to module scope so it can be
 * evaluated under two different flag sets: the nav the operator is being shown,
 * and the nav they would have been shown on an all-OFF read. The difference
 * between those two is what the unreadable-entitlement notice is about.
 *
 * Role names arrive lowercase because platform-admin-api proxies aaim-core
 * login through its role-normalizing helper (routes/auth.routes.ts) before
 * landing in the auth store. Canonical role
 * names in seed-rbac.ts are FI_ADMIN / FI_LOAN_OFFICER / FI_COMPLIANCE /
 * FI_VIEWER / FI_TREASURY (FI-neutral, covering credit unions, community
 * banks, regional banks, etc.).
 * Atomic squash of the earlier cu_-prefixed literals on 2026-05-10 per
 * feedback_no_pre_customer_tech_debt: pre-customer, no in-flight tokens carry
 * cu_* roles, so a clean cut is safer than a transition window.
 *
 * A caller holding several FI roles takes the first match, widest first.
 * fi_treasury precedes fi_viewer because its catalog set contains the
 * viewer's except members:read and adds participation:commit and
 * compliance:read. The two pilot operator roles come last: every FI builder
 * above already opens the Pledge Inbox, and pledge_reviewer precedes
 * pledgor_advisor because the two sets share no permission and the reviewer's
 * queue is the step-8 surface; neither builder contains the other.
 *
 * Default branch: platform admins (aaim_super_admin, e2e_test_admin) and
 * unknown FI-tenant roles all receive the full FI_ADMIN nav for the apex
 * tenant. The org switcher handles impersonation; platform system config is
 * canonical at platform-admin (9303). fi_viewer and fi_treasury took this
 * branch until GH #2847, pledge_reviewer and pledgor_advisor until GH #2318.
 *
 * Exported for PortalLayout.nav-reach.test.tsx, which pins each role's reach.
 */
export function buildGroupsForRoles(
  roles: string[],
  flags: InstitutionFlags,
): NavGroupDefinition[] {
  if (roles.includes('fi_admin')) return buildFiTenantGroups(flags);
  if (roles.includes('fi_loan_officer')) return buildLoanOfficerGroups(flags);
  if (roles.includes('fi_compliance')) return buildComplianceGroups(flags);
  if (roles.includes('fi_treasury')) return buildTreasuryGroups(flags);
  if (roles.includes('fi_viewer')) return buildViewerGroups(flags);
  if (roles.includes('pledge_reviewer')) return buildPledgeReviewerGroups();
  if (roles.includes('pledgor_advisor')) return buildPledgorAdvisorGroups(flags);
  return buildFiTenantGroups(flags);
}

// ---------------------------------------------------------------------------
// Command-palette reachability gate (GH #1729 deliverable 2(b))
//
// SEARCHABLE_PAGES is one flat static list and handleSearch used to filter it
// by title substring alone. Nothing about the persona entered the decision, so
// Cmd+K was a role-blind index of the whole portal: an fi_compliance operator
// could type "Organization Profile" and land on an FI Configuration page their
// sidebar deliberately withholds, and an fi_loan_officer could reach ABAC
// Policies the same way. The sidebar's gate was real; the palette sat beside it
// with no gate at all.
//
// The palette now answers the same question the sidebar answers: is this path
// reachable from the nav THIS persona is being shown, under the flags they are
// being shown it under. Derived from the built groups rather than from a second
// role table, so a builder change moves both surfaces at once and they cannot
// drift apart.
//
// This is a visibility gate, not an authorization boundary. Server-side gates
// remain the enforcement point -- a deep-typed URL still reaches the route, and
// the API behind it still decides.
// ---------------------------------------------------------------------------

/**
 * Case-insensitive ANY-of gate check, mirroring RoleBasedNav.hasAccess
 * (packages/ui/src/portal/RoleBasedNav.tsx). Duplicated rather than imported
 * because the UI package exports the component, not the predicate; the two must
 * agree, so this reads the same literals off the same NavGroupDefinition and
 * lowercases both sides per .claude/rules/authz-case-insensitive.md.
 */
function normalizeGate(literal: NavItemDefinition['requiredPermission']): string[] {
  if (literal == null) return [];
  if (typeof literal === 'string') return [literal.toLowerCase()];
  if (!Array.isArray(literal)) return [];
  return literal
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.toLowerCase());
}

function navHasAccess(
  permissions: string[],
  roles: string[],
  requiredPermission: NavItemDefinition['requiredPermission'],
  requiredRole: NavItemDefinition['requiredRole'],
): boolean {
  const permTargets = normalizeGate(requiredPermission);
  if (
    permTargets.length > 0 &&
    !permissions.some((p) => typeof p === 'string' && permTargets.includes(p.toLowerCase()))
  ) {
    return false;
  }
  const roleTargets = normalizeGate(requiredRole);
  if (
    roleTargets.length > 0 &&
    !roles.some((r) => typeof r === 'string' && roleTargets.includes(r.toLowerCase()))
  ) {
    return false;
  }
  return true;
}

/**
 * Every path this persona can reach from the sidebar they are actually shown:
 * group gates first, then item gates, then NAV_ID_TO_PATH. A group or item the
 * persona cannot see contributes nothing.
 */
export function reachableNavPaths(
  groups: NavGroupDefinition[],
  permissions: string[],
  roles: string[],
): Set<string> {
  const paths = new Set<string>();
  const walk = (items: NavItemDefinition[]): void => {
    for (const item of items) {
      if (!navHasAccess(permissions, roles, item.requiredPermission, item.requiredRole)) continue;
      const path = NAV_ID_TO_PATH[item.id];
      if (path) paths.add(path);
      if (item.children) walk(item.children);
    }
  };
  for (const group of groups) {
    if (!navHasAccess(permissions, roles, group.requiredPermission, group.requiredRole)) continue;
    walk(group.items);
  }
  return paths;
}

/**
 * The palette entries a persona may see. Exported so the gate is testable as
 * the pure function it is, rather than only through a Cmd+K interaction.
 */
export function paletteEntriesFor(
  roles: string[],
  permissions: string[],
  flags: InstitutionFlags,
): typeof SEARCHABLE_PAGES {
  const reachable = reachableNavPaths(buildGroupsForRoles(roles, flags), permissions, roles);
  return SEARCHABLE_PAGES.filter((page) => reachable.has(page.href));
}

/**
 * The flag set the nav renders under when the entitlement read FAILED: the four
 * nav-gating flags ON. `palEnabled` stays at its placeholder value because no
 * builder reads it -- forcing a flag that gates API routes would assert an
 * entitlement rather than decline to assert one.
 */
const NAV_FLAGS_ON_UNREADABLE: InstitutionFlags = {
  ...DEFAULT_INSTITUTION_FLAGS,
  enableLosPreview: true,
  enablePeValuation: true,
  enableLoanParticipations: true,
  enableOverdraftPreview: true,
};

/** Structural fingerprint of a nav: group ids and their item ids, in order. */
function navShape(groups: NavGroupDefinition[]): string {
  return groups
    .map((g) => `${g.id}:${g.items.map((i) => i.id).join(',')}`)
    .join('|');
}

/**
 * PortalLayout - Modernized admin portal layout using UI Kit components.
 *
 * Architecture:
 * - AppHeader: Top navigation with breadcrumbs and action toolbar
 * - RoleBasedNav: Sidebar navigation with role/permission-driven visibility
 * - CommandPalette: Cmd+K command palette with search across pages, loans, members, tenants, compliance
 * - Breadcrumbs: Inline in AppHeader via breadcrumbs prop
 *
 * This is the sole layout for the admin portal. MainLayout has been removed.
 */
export function PortalLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // GH #3253: the searchable tenant picker the user menu opens below lg.
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  // Sprint D Lane D1 (2026-05-17, F-XPORTAL-MOCK-JWT-001 closure): visible
  // error surface for impersonation 401/403/500 cascades. The prior
  // implementation only logged to console.error, so a tenant switch that
  // failed silently dropped the operator back to the prior tenant with no
  // diagnostic. The banner renders above the page content area and is
  // cleared on the next successful tenant switch or manual dismiss.
  const [impersonationError, setImpersonationError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout, setImpersonation } = useAuthStore();

  // -------------------------------------------------------------------------
  // Tenant context and the two portals (2026-05-17)
  //
  // admin-portal (port 9300) is the tenant-scoped surface. It serves any
  // authenticated user under their resolved tenant context:
  //   - FI tenant operators (FI_ADMIN / FI_LOAN_OFFICER / FI_COMPLIANCE)
  //     under their FI's tenantId
  //   - Apex Aaim platform admins (AAIM_SUPER_ADMIN / E2E_TEST_ADMIN)
  //     under the Aaim demo tenant when no FI impersonation is active.
  //     They use 9300 to operate as the Aaim demo tenant for sales
  //     demos, dogfooding, and self-QA.
  //   - Apex Aaim platform admins under impersonation (via OrgSwitcher)
  //     to assist a specific FI with their config or data.
  //
  // platform-admin (port 9303) is the cross-tenant platform-wide surface.
  // It hosts Tenants roster, Platform Configuration, Asset Taxonomy,
  // Identity & Auth, etc. Apex platform admins choose between the two
  // surfaces based on the task -- the app does not auto-redirect.
  //
  // Note: there is no auto-redirect here. A prior implementation (Sprint
  // F-FI-ADMIN-AUTH-LOOP, 2026-05-17) bounced apex sessions to 9303,
  // which broke the apex+demo and apex+impersonation flows. The redirect
  // was removed 2026-05-17 after the user flagged that it broke the
  // ability to use 9300 against the Aaim demo tenant and to impersonate
  // an FI scope from apex.
  // -------------------------------------------------------------------------
  // Breadcrumbs - derive from current route
  // -------------------------------------------------------------------------

  const currentPageInfo = useMemo(
    (): PageInfo => resolvePageInfo(location.pathname, location.state),
    [location.pathname, location.state],
  );

  // -------------------------------------------------------------------------
  // RoleBasedNav group selection
  // -------------------------------------------------------------------------

  // Tenant-scoped product-surface flags (Sprint C14 WP A-1). Four flags gate
  // conditional nav surfaces: enableLosPreview gates the Lending (Preview)
  // group, enableLoanParticipations gates the Loan Participations group,
  // enableOverdraftPreview gates BOTH the Aaim Tempo and Economic Modeling groups
  // (one flag, two programs -- operator ruling 2026-08-26, GH #2727), and
  // enablePeValuation gates the item-level PE Valuation surfaces nested within
  // FI Configuration and Collateral Monitoring (GH #676, 2026-08-15 relocation
  // -- no longer a standalone group). `palEnabled` is NOT a nav gate: it gates
  // API routes only (KG platform-nav-scope 2026-05-24, decision 4).
  //
  // GH #2742 class -- this was the FIFTH consumer reading the all-OFF
  // placeholder as a decision. The prior line destructured `flags`, which the
  // hook returns as `data ?? DEFAULT_INSTITUTION_FLAGS`, so a 500, a network
  // drop, or an unparseable body produced four confident `false` entitlement
  // facts and SILENTLY erased four product groups from the nav. The operator
  // saw a shorter sidebar with nothing said, and no reason to suspect the read
  // rather than their contract.
  //
  // The disposition here is PERMISSIVE, and it is deliberately the opposite of
  // the PeFeatureGate route gate (routes/index.tsx), which blocks. The two
  // differ because the cost of being wrong differs. A route gate that renders
  // on an unreadable flag MOUNTS pages that call unprovisioned endpoints
  // (F-AP-PE-PARSE-001). A nav entry mounts nothing -- it is a link. Showing it
  // costs at most one click into a server-side guard that still enforces the
  // real entitlement; hiding it removes the operator's only evidence that the
  // surface exists. So the nav shows the groups and SAYS the read failed, and
  // the route gate behind each one remains the enforcement point.
  const {
    flagsRead,
    flagsUnreadable,
    flagsError,
    refetch: refetchInstitutionFlags,
  } = useInstitutionFlags();

  // On every path other than the failed read this is byte-identical to the
  // previous `flags`: a successful read returns the read, and the loading
  // window returns the all-OFF placeholder exactly as before.
  const institutionFlags: InstitutionFlags = useMemo(
    () =>
      flagsUnreadable
        ? NAV_FLAGS_ON_UNREADABLE
        : flagsRead ?? DEFAULT_INSTITUTION_FLAGS,
    [flagsUnreadable, flagsRead],
  );

  const baseNavGroups: NavGroupDefinition[] = useMemo(
    () => buildGroupsForRoles(user?.roles ?? [], institutionFlags),
    [user?.roles, institutionFlags],
  );

  // Does THIS persona's nav actually carry flag-gated surfaces? buildCompliance
  // Groups ignores flags entirely, so an fi_compliance operator's nav is
  // identical under a failed read -- telling them product surfaces are shown
  // unverified would name surfaces they never had. Derived by comparing the two
  // builds rather than by listing roles here, so a builder that gains or loses
  // a flag gate stays correct with no edit at this site.
  const navVariesWithFlags = useMemo(() => {
    const roles = user?.roles ?? [];
    return (
      navShape(buildGroupsForRoles(roles, NAV_FLAGS_ON_UNREADABLE)) !==
      navShape(buildGroupsForRoles(roles, DEFAULT_INSTITUTION_FLAGS))
    );
  }, [user?.roles]);

  // -------------------------------------------------------------------------
  // FI Tasks badge -- polls /admin/tasks/count every 60s to keep the badge warm.
  // Gate the poll on the actual pledge:read:inbox permission, not merely on the
  // tasks nav entry being present: FI_ADMIN surfaces the Tasks nav but does NOT
  // hold pledge:read:inbox, so a nav-presence-only gate polls and 403s every 60s
  // (the 2026-07-20 console-noise class). Platform admins operating at apex keep
  // the poll (the ComplianceReports authz idiom). Case-insensitive compare per
  // .claude/rules/authz-case-insensitive.md.
  // -------------------------------------------------------------------------

  const hasTasksNav = useMemo(
    () =>
      baseNavGroups.some((group) =>
        group.items.some((item) => item.id === 'tasks'),
      ),
    [baseNavGroups],
  );

  const canReadTaskInbox = useMemo(() => {
    return (
      hasPlatformAdminRole(user?.roles) ||
      (user?.permissions ?? []).some((p) => p.toLowerCase() === 'pledge:read:inbox')
    );
  }, [user?.roles, user?.permissions]);

  const { data: taskCountData } = useTaskCount({
    enabled: hasTasksNav && canReadTaskInbox,
  });

  const pendingCount = taskCountData?.pendingCount ?? 0;
  const overSlaCount = taskCountData?.overSlaCount ?? 0;

  // -------------------------------------------------------------------------
  // Pending IdP Claims badge (Sprint 5 S5-A) -- the FI Admin's "queue depth"
  // signal for upstream IdP role strings that have not yet been mapped to
  // an Aaim Role. Polling cadence 30s. Disabled when the nav entry is not
  // present (FI Loan Officer / FI Compliance roles do not surface it).
  //
  // The query is bindingId-scoped. The FI Admin's tenant currently carries
  // a single primary IdP binding; the binding id is read from the auth
  // store's user payload when available. If it is not available the hook
  // is disabled and the badge stays hidden -- never block the nav on a
  // missing claim.
  // -------------------------------------------------------------------------

  const hasIdpPendingClaimsNav = useMemo(
    () =>
      baseNavGroups.some((group) =>
        group.items.some((item) => item.id === 'admin-idp-pending-claims'),
      ),
    [baseNavGroups],
  );

  const primaryIdpBindingId = (
    user as unknown as { primaryIdpBindingId?: string } | null | undefined
  )?.primaryIdpBindingId;

  const { data: idpPendingClaimCount } = useIdpPendingClaimCount(
    primaryIdpBindingId,
    { enabled: hasIdpPendingClaimsNav },
  );

  const idpPendingCount = idpPendingClaimCount?.pendingCount ?? 0;

  const navGroups: NavGroupDefinition[] = useMemo(() => {
    if (!hasTasksNav && !hasIdpPendingClaimsNav) return baseNavGroups;
    return baseNavGroups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.id === 'tasks') {
          return {
            ...item,
            badgeNode: (
              <NavBadge
                pendingCount={pendingCount}
                overSlaCount={overSlaCount}
              />
            ),
          };
        }
        if (item.id === 'admin-idp-pending-claims') {
          return {
            ...item,
            badgeNode: (
              <NavBadge
                pendingCount={idpPendingCount}
                overSlaCount={0}
              />
            ),
          };
        }
        return item;
      }),
    }));
  }, [
    baseNavGroups,
    hasTasksNav,
    hasIdpPendingClaimsNav,
    pendingCount,
    overSlaCount,
    idpPendingCount,
  ]);

  // -------------------------------------------------------------------------
  // Organization data
  //
  // Sprint C12-followon-remediation: the OrgSwitcher is driven by an
  // RBAC-aware "tenants this user can enter" set returned from
  // GET /tenants/accessible, NOT by an isPlatformAdmin role check. This
  // generalizes correctly to:
  //   - Platform admins  -> apex + all FI tenants (>1 entries -> visible)
  //   - Multi-org FI ops -> their N tenants (>1 entries -> visible) [v1.5
  //                          fallback returns single tenant; multi-org join
  //                          deferred to C12FR->C13-6]
  //   - Single-org FI ops -> 1 tenant (<=1 entry -> dropdown self-hides)
  //
  // The dropdown is rendered conditionally below based on the size of the
  // accessible set. The component itself does not decide visibility -- the
  // layout decides whether to render it.
  // -------------------------------------------------------------------------

  const isImpersonating = !!user?.impersonatedBy;

  const { data: accessibleTenants } = useAccessibleTenants();

  // #2293: derive { currentOrg, organizations } via the pure resolveOrgContext
  // helper (org-context.ts). While impersonating, `organizations` always carries
  // the apex exit row + the current org even when accessible-tenants 401s or
  // omits the impersonated tenant, so the OrgSwitcher (gated on length > 1
  // below) keeps rendering and "exit impersonation" stays reachable; and
  // currentOrg is seeded with a legible label rather than '(unknown tenant)'.
  const { currentOrg, organizations, unresolved } = useMemo(
    () =>
      resolveOrgContext(
        user as unknown as import('./org-context').OrgContextUser | null | undefined,
        accessibleTenants?.data,
      ),
    [user, accessibleTenants?.data],
  );

  // Preserve the drift signal: only the genuinely-unresolvable NON-impersonation
  // case warns (an FI tenantId absent from the accessible list). Impersonation
  // seeds a legible label and never trips this.
  useEffect(() => {
    if (unresolved) {
      // eslint-disable-next-line no-console
      console.warn(
        `[PortalLayout] tenantId ${currentOrg.id} not in accessible-tenants list. ` +
        'Either the user lacks RBAC for this tenant or the accessible cache is stale.',
      );
    }
  }, [unresolved, currentOrg.id]);

  const handleOrgChange = useCallback(
    (org: Organization) => {
      if (org.id === user?.tenantId) return; // No-op if already in this tenant
      setImpersonationError(null);
      void (async () => {
        try {
          const result = await tenantsApi.impersonate({ tenantId: org.id });
          // Purge cached tenant-scoped data so the operator does not see
          // stale rows from the previous tenant.
          queryClient.clear();
          // N3b (GH #1806): the impersonate response rotates the HttpOnly session
          // cookie; the body no longer carries a token. Apply only the user record
          // (setImpersonation ignores the token argument and re-hydrates from cookie).
          setImpersonation({
            user: result.user,
          });
          // Land on dashboard so the operator's first impression of the
          // target tenant context is the home page, not whatever deep
          // link they happened to be on.
          navigate('/');
        } catch (err) {
          // Sprint D Lane D1 (F-XPORTAL-MOCK-JWT-001 closure): surface the
          // failure to the operator. Prior implementation only logged to
          // console.error, which presented as a silent revert to the prior
          // tenant. tenantsApi.impersonate constructs the message as
          // `${code}: ${message}` from the platform-admin-api proxy
          // response (which forwards aaim-core /api/v1/auth/impersonate),
          // so the banner reflects the upstream error verbatim.
          const message = err instanceof Error ? err.message : String(err);
          setImpersonationError(`Impersonation failed: ${message}`);
        }
      })();
    },
    [user?.tenantId, queryClient, setImpersonation, navigate],
  );

  // -------------------------------------------------------------------------
  // User profile for top nav
  // -------------------------------------------------------------------------

  // F-XPORTAL-AVATAR-001 (2026-05-16): omit `initials` so the canonical
  // two-character extraction in @aaimplatform/ui UserProfileMenu
  // (getInitials) is used. Passing a single-char override defeated the
  // package logic and rendered "R" instead of "RG" for "Robert Goodyear".
  const userProfile: UserProfile = useMemo(() => ({
    name: user?.name || 'User',
    email: user?.email || 'user@example.com',
    role: user?.roles?.[0] || 'N/A',
  }), [user?.name, user?.email, user?.roles]);

  const userMenuItems: UserMenuItem[] = useMemo(() => [
    { id: 'profile', label: 'Profile', onClick: () => navigate('/profile') },
    // 'Settings' item removed 2026-06-19 (nav-tree audit H7): it navigated to
    // /admin/platform/security, which has no admin-portal route (platform config
    // moved to platform-admin), so it dead-ended on NotFoundPage for every role.
  ], [navigate]);

  // Sign-out, by hand or after 30 minutes without input (DCF-62), also drops
  // the query cache: the credential sign-in stays in this page, so cached
  // rows would otherwise show the next person at this tab the previous
  // user's tenant data.
  const handleSignOut = useCallback(() => {
    logout();
    queryClient.clear();
    navigate('/login');
  }, [logout, queryClient, navigate]);

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------

  // GH #2281: the bell is fed by the real notification inbox
  // (/api/v1/notifications, notification-inbox.openapi.yaml) -- the
  // fi_officer/fi_ops rows the outbox handler writes. The prior hardcoded
  // three-row stub array is DELETED (the pubsub design doc's retirement
  // schedule row 1); a component test pins the wiring so the stub cannot
  // silently return.
  const { data: notificationFeed } = useNotifications(
    { pageSize: 10 },
    { refetchIntervalMs: 60_000 },
  );
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();

  const notifications: Notification[] = useMemo(
    () =>
      (notificationFeed?.data ?? []).map((row) => ({
        id: row.id,
        title: row.title,
        message: row.body,
        // The instant, formatted by the panel in the viewer's zone with the
        // same formatter as the Notifications page (GH #2296).
        timestamp: row.createdAt,
        unread: !row.readAt,
        onClick: () => {
          markNotificationRead.mutate(row.id);
          if (row.pledgeId) {
            navigate(`/pledges/${row.pledgeId}`);
          }
        },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation identity is stable per @tanstack/react-query
    [notificationFeed, navigate],
  );

  // -------------------------------------------------------------------------
  // Global Search
  // -------------------------------------------------------------------------

  // GH #1729 deliverable 2(b): the palette indexes only what this persona's own
  // nav can reach, under the same flags the nav is rendered with (so a failed
  // entitlement read shows the same surfaces in both places). Both the search
  // and the select read this list -- gating only the search would leave a stale
  // result able to navigate.
  const paletteEntries = useMemo(
    () => paletteEntriesFor(user?.roles ?? [], user?.permissions ?? [], institutionFlags),
    [user?.roles, user?.permissions, institutionFlags],
  );

  const handleSearch = useCallback(async (query: string, category?: string): Promise<PaletteResult[]> => {
    const lowerQuery = query.toLowerCase();
    return paletteEntries
      .filter(page => {
        const matchesQuery = page.title.toLowerCase().includes(lowerQuery);
        const matchesCategory = !category || page.category === category;
        return matchesQuery && matchesCategory;
      })
      .map(({ id, title, category: cat }) => ({ id, title, category: cat }));
  }, [paletteEntries]);

  const handleSearchSelect = useCallback((result: PaletteResult) => {
    const match = paletteEntries.find(p => p.id === result.id);
    if (match?.href) {
      navigate(match.href);
    }
  }, [paletteEntries, navigate]);

  // -------------------------------------------------------------------------
  // RoleBasedNav - active item and selection
  // -------------------------------------------------------------------------

  const activeNavItemId = useMemo(
    () => resolveActiveNavItemId(location.pathname, location.state),
    [location.pathname, location.state],
  );

  const handleNavItemSelect = useCallback((id: string) => {
    const path = NAV_ID_TO_PATH[id];
    if (path) {
      navigate(path);
      setMobileOpen(false);
    }
  }, [navigate]);

  // -------------------------------------------------------------------------
  // Sidebar content (shared between desktop and mobile)
  // -------------------------------------------------------------------------

  const sidebarHeader = useMemo(() => (
    <div className="h-[65px] flex items-center px-4 border-b border-recessed">
      <img
        src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
        alt="Aaim"
        className="h-6"
      />
      <span className="ml-2 text-xs font-sans text-tertiary">
        Admin Portal
      </span>
    </div>
  ), []);

  // GH #2742 class -- the nav's half of the unreadable-entitlement contract.
  // The four flag-gated groups above are rendered VISIBLE when the read fails,
  // so without this the operator would see a nav indistinguishable from a fully
  // entitled one and would read four unverified surfaces as contracted fact.
  // Showing the groups and saying nothing trades one silent wrong answer for
  // the opposite silent wrong answer.
  //
  // Compact by necessity: this sits in a 260px sidebar. `warning` carries the
  // operator's actual condition -- the nav functions and every surface stays
  // reachable, with the entitlement behind each one unverified. `error` would
  // overstate a state the operator can still work in. Composed from
  // @aaimplatform/ui Alert + Button per the FRONTEND GOVERNANCE mandate; the
  // wording follows the PeFeatureGate notice (routes/index.tsx) so an operator
  // who meets both reads the same sentence about the same failure.
  const entitlementNotice = useMemo(() => {
    if (!flagsUnreadable || !navVariesWithFlags) return null;
    return (
      <div className="px-3 py-3 border-b border-recessed" role="alert">
        <Alert type="warning" title="Entitlements could not be verified">
          <div className="space-y-2">
            <div className="text-xs">
              {flagsError instanceof Error
                ? flagsError.message
                : 'Entitlements could not be read.'}
            </div>
            <div className="text-xs">
              Product surfaces are shown unverified. This does not mean your
              institution holds them -- the read itself failed. Access is still
              enforced when you open one.
            </div>
            <Button
              variant="tertiary"
              size="small"
              onClick={() => {
                void refetchInstitutionFlags();
              }}
            >
              Retry
            </Button>
          </div>
        </Alert>
      </div>
    );
  }, [flagsUnreadable, navVariesWithFlags, flagsError, refetchInstitutionFlags]);

  const sidebarFooter = useMemo(() => (
    <div className="px-4 py-3 border-t border-recessed">
      <BuildVersion
        buildInfo={{ version: import.meta.env.VITE_BUILD_VERSION || '0.0.0-dev' }}
        format="short"
      />
    </div>
  ), []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // FI-TENANT nav (admin.aaim.com:9300 -- FI_ADMIN / FI_LOAN_OFFICER /
  // FI_COMPLIANCE). This node holds this portal's own nav groups, permissions
  // and roles, and is passed into PortalFrame's `sidebar` slot. The frame holds
  // no persona, tenant or permission knowledge; sharing the frame with
  // platform.aaim.com:9303 (Aaim-operator) does NOT share the navigation.
  // See .claude/rules/scope-orientation-c4-first.md.
  const fiTenantNav = (
    <RoleBasedNav
      groups={navGroups}
      activeItemId={activeNavItemId}
      onItemSelect={handleNavItemSelect}
      permissions={user?.permissions ?? []}
      roles={user?.roles ?? []}
      density="default"
      header={
        <>
          {sidebarHeader}
          {entitlementNotice}
        </>
      }
      footer={sidebarFooter}
    />
  );

  return (
    <PortalFrame
      overlays={
        /* Command Palette (renders as modal overlay, activated by Cmd+K) */
        <CommandPalette
          categories={SEARCH_CATEGORIES}
          onSearch={handleSearch}
          onSelect={handleSearchSelect}
          placeholder="Search pages, loans, members, tenants, compliance..."
        />
      }
      sidebar={fiTenantNav}
      mobileOpen={mobileOpen}
      onMobileClose={() => setMobileOpen(false)}
      header={
        /* Top Navigation Bar */
        <AppHeader
          title={currentPageInfo.title}
          leading={
            /* Sidebar toggle: top-left, BEFORE the title (it opens the sidebar
               drawer, so it sits where the sidebar is). Mobile only. */
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 -ml-2 text-secondary hover:text-primary"
              aria-label="Open menu"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
          }
          actions={
            <>
              <CommandPaletteTrigger />
              {organizations.length > 1 && (
                <OrgSwitcher
                  currentOrg={currentOrg}
                  organizations={organizations}
                  onOrgChange={handleOrgChange}
                  impersonating={isImpersonating}
                  className="hidden lg:flex"
                />
              )}
              <NotificationPanel
                notifications={notifications}
                /* The badge prints the server's unread total across every
                   page, not a count of the 10 rows the bell loads, so it
                   agrees with the Notifications page. */
                unreadCount={notificationFeed?.unreadCount}
                onMarkAllRead={() => markAllNotificationsRead.mutate()}
                onViewAll={() => navigate('/notifications')}
              />
              {/* Placement convention (GH #2305): last icon affordance before
                  the user pill, on every institutional SPA. */}
              <ThemeToggle />
              <UserProfileMenu
                user={userProfile}
                menuItems={userMenuItems}
                onSignOut={handleSignOut}
                impersonating={isImpersonating}
                showEmail
                showRole
                /* GH #3253: the header OrgSwitcher above is hidden below lg,
                   so the user menu carries one switch entry there. It opens
                   the searchable OrgSwitcherDialog, which scales to any
                   number of tenants. */
                orgSection={
                  organizations.length > 1
                    ? {
                        currentOrg,
                        onSwitchRequest: () => setOrgDialogOpen(true),
                        className: 'lg:hidden',
                      }
                    : undefined
                }
              />
              {organizations.length > 1 && (
                <OrgSwitcherDialog
                  open={orgDialogOpen}
                  onOpenChange={setOrgDialogOpen}
                  currentOrg={currentOrg}
                  organizations={organizations}
                  onOrgChange={handleOrgChange}
                />
              )}
            </>
          }
          sticky
          backdrop
        />
      }
      beforeMain={
        /* Impersonation error banner (Sprint D Lane D1). Rendered between the
           header and <main>, inside the scroll flow, exactly as before -- it
           still pushes content down and scrolls away. Making it persistent is
           AAIM-INST-038, not this extraction. */
        impersonationError ? (
          <div className="mx-4 mt-4 md:mx-6 flex items-start gap-3">
            <div className="flex-1">
              <Alert type="error" title="Impersonation failed">
                {impersonationError}
              </Alert>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setImpersonationError(null)}
            >
              Dismiss
            </Button>
          </div>
        ) : null
      }
    >
      {/* DCF-62: sign out after 30 minutes without input (Robert's card of
          2026-11-02 19:16:47Z). The task-count poll is not input, so an open,
          unattended window signs out too. */}
      <IdleSignOut onSignOut={handleSignOut} channelName={IDLE_SIGN_OUT_CHANNEL} />
      {/* Page Content */}
      <Outlet />
    </PortalFrame>
  );
}
